import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { firestore, database, storage } from "../firebaseConfig";
import { collection, query, where, orderBy, getDocs, doc, updateDoc, addDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { ref, onValue, set as rtdbSet } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { useActiveWorld } from "../hooks/useActiveWorld";
import { autoEvaluateCondition } from "./warmupFlowUtils";
import { buildSearchTerms } from "./searchUtils";
import { logSalesAnalytics, logOutcomeAnalytics } from "./analyticsTracker";
import { describeEdit, logContactActivity } from "../shared/contactActivityLog";
import {
  createPrivateNote,
  normalizeLeadNotes,
  leadNotesToText,
  getActivityConfig,
  getOutcomeConfig,
  getSentimentConfig,
} from "./NotesActivityFields";
import { Letter } from 'react-letter';
import "./Leads.css";
import LeadsEmptyStart from "./LeadsEmptyStart";

// ========== KONSTANTEN (außerhalb der Komponente) ==========

// Alle verfügbaren Touchpoints für Lead-Interaktionen (mit Gewichtung und Gruppierung)
const TOUCHPOINT_GROUPS = {
  meetings: {
    label: "Meetings & Calls",
    icon: "fa-calendar-check",
    items: {
      zoom_call_scheduled: { label: "Zoom Call Scheduled", icon: "fa-video", points: 3 },
      meet_in_person: { label: "Meet In Person", icon: "fa-users", hasCount: true, points: 4 },
      sales_meeting: { label: "Sales Meeting", icon: "fa-handshake", hasCount: true, points: 4 },
      online_meeting: { label: "Online Meeting", icon: "fa-video", hasCount: true, points: 3 },
      video_call: { label: "Video Call", icon: "fa-video", hasCount: true, points: 3 },
      phone_call: { label: "Phone Call", icon: "fa-phone-alt", hasCount: true, points: 2 },
      lunch_meeting: { label: "Lunch Meeting", icon: "fa-utensils", hasCount: true, points: 4 },
      networking_event: { label: "Networking Event", icon: "fa-glass-cheers", hasCount: true, points: 3 },
      discovery_call: { label: "Discovery Call Done", readyLabel: "Discovery Call", icon: "fa-search", hasCount: true, points: 3 },
      follow_up_call: { label: "Follow Up Call Done", readyLabel: "Follow Up Call", icon: "fa-phone-volume", hasCount: true, points: 2 },
    }
  },
  sales: {
    label: "Sales Process",
    icon: "fa-chart-line",
    items: {
      interested_in_groupchat: { label: "Interested in Groupchat", icon: "fa-comments", points: 2 },
      demo_given: { label: "Demo Given", readyLabel: "Give Demo", icon: "fa-play-circle", hasCount: true, points: 3 },
      proposal_sent: { label: "Proposal Sent", readyLabel: "Send Proposal", icon: "fa-file-alt", hasCount: true, points: 3 },
      quote_sent: { label: "Quote Sent", readyLabel: "Send Quote", icon: "fa-file-invoice-dollar", hasCount: true, points: 2 },
      contract_sent: { label: "Contract Sent", readyLabel: "Send Contract", icon: "fa-file-contract", points: 3 },
      contract_signed: { label: "Contract Signed", icon: "fa-file-signature", points: 5 },
      invoice_sent: { label: "Invoice Sent", readyLabel: "Send Invoice", icon: "fa-file-invoice", points: 2 },
      payment_received: { label: "Payment Received", icon: "fa-money-check-alt", points: 5 },
      upsell_offer: { label: "Upsell Offer", readyLabel: "Make Upsell Offer", icon: "fa-arrow-up", hasCount: true, points: 2 },
      onboarding: { label: "Onboarding Done", readyLabel: "Onboarding", icon: "fa-rocket", points: 4 },
    }
  },
  email: {
    label: "Email & Messages",
    icon: "fa-envelope",
    items: {
      email_sent: { label: "Email Sent", readyLabel: "Send Email", icon: "fa-paper-plane", hasCount: true, points: 1 },
      cold_email: { label: "Cold Email Sent", readyLabel: "Send Cold Email", icon: "fa-snowflake", hasCount: true, points: 1 },
      follow_up_email: { label: "Follow Up Email Sent", readyLabel: "Send Follow Up Email", icon: "fa-reply", hasCount: true, points: 1 },
      nurture_email: { label: "Nurture Email Sent", readyLabel: "Send Nurture Email", icon: "fa-heart", hasCount: true, points: 1 },
      reminder_email: { label: "Reminder Email Sent", readyLabel: "Send Reminder Email", icon: "fa-bell", hasCount: true, points: 1 },
      case_study_email: { label: "Case Study Email Sent", readyLabel: "Send Case Study Email", icon: "fa-chart-bar", hasCount: true, points: 1.5 },
      testimonial_email: { label: "Testimonial Email Sent", readyLabel: "Send Testimonial Email", icon: "fa-star", hasCount: true, points: 1.5 },
      newsletter_sent: { label: "Newsletter Sent", readyLabel: "Send Newsletter", icon: "fa-newspaper", hasCount: true, points: 0.5 },
      newsletter: { label: "Newsletter Sent", readyLabel: "Send Newsletter", icon: "fa-newspaper", hasCount: true, points: 0.5 },
      email_opened: { label: "Email Opened", icon: "fa-envelope-open", hasCount: true, points: 0.5 },
      email_replied: { label: "Email Replied", icon: "fa-reply-all", hasCount: true, points: 2 },
      sms_sent: { label: "SMS Sent", readyLabel: "Send SMS", icon: "fa-sms", hasCount: true, points: 1 },
    }
  },
  linkedin: {
    label: "LinkedIn",
    icon: "fa-linkedin",
    isBrand: true,
    items: {
      linkedin_connect: { label: "Connection Request", readyLabel: "Send Connection Request", icon: "fa-linkedin", isBrand: true, points: 0.5 },
      linkedin_connected: { label: "Connected", icon: "fa-linkedin", isBrand: true, points: 1 },
      linkedin_message: { label: "Message Sent", readyLabel: "Send Message", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 1 },
      linkedin_voice: { label: "Voice Message", readyLabel: "Send Voice Message", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 1.5 },
      linkedin_like: { label: "Post Liked", readyLabel: "Like Post", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 0.5 },
      linkedin_comment: { label: "Post Commented", readyLabel: "Comment on Post", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 1 },
      linkedin_share: { label: "Post Shared", readyLabel: "Share Post", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 1 },
      linkedin_endorsed: { label: "Skill Endorsed", readyLabel: "Endorse Skill", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 0.5 },
      linkedin_recommendation: { label: "Recommendation", readyLabel: "Write Recommendation", icon: "fa-linkedin", isBrand: true, points: 2 },
    }
  },
  instagram: {
    label: "Instagram",
    icon: "fa-instagram",
    isBrand: true,
    items: {
      instagram_follow: { label: "Followed", readyLabel: "Follow", icon: "fa-instagram", isBrand: true, points: 0.5 },
      instagram_followed_back: { label: "Followed Back", icon: "fa-instagram", isBrand: true, points: 1 },
      instagram_dm: { label: "DM Sent", readyLabel: "Send DM", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1 },
      instagram_voice: { label: "Voice DM", readyLabel: "Send Voice DM", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1.5 },
      instagram_like: { label: "Post Liked", readyLabel: "Like Post", icon: "fa-instagram", isBrand: true, hasCount: true, points: 0.5 },
      instagram_comment: { label: "Post Commented", readyLabel: "Comment on Post", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1 },
      instagram_story_reply: { label: "Story Reply", readyLabel: "Reply to Story", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1 },
      instagram_story_mention: { label: "Story Mention", readyLabel: "Mention in Story", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1.5 },
      instagram_reel_comment: { label: "Reel Comment", readyLabel: "Comment on Reel", icon: "fa-instagram", isBrand: true, hasCount: true, points: 1 },
      instagram_collab: { label: "Collab Post", readyLabel: "Create Collab Post", icon: "fa-instagram", isBrand: true, points: 3 },
    }
  },
  whatsapp: {
    label: "WhatsApp",
    icon: "fa-whatsapp",
    isBrand: true,
    items: {
      whatsapp_message: { label: "Message Sent", readyLabel: "Send Message", icon: "fa-whatsapp", isBrand: true, hasCount: true, points: 1 },
      whatsapp_voice: { label: "Voice Message", readyLabel: "Send Voice Message", icon: "fa-whatsapp", isBrand: true, hasCount: true, points: 1.5 },
      whatsapp_call: { label: "WhatsApp Call", readyLabel: "Make WhatsApp Call", icon: "fa-whatsapp", isBrand: true, hasCount: true, points: 2 },
      whatsapp_video: { label: "Video Call", readyLabel: "Make Video Call", icon: "fa-whatsapp", isBrand: true, hasCount: true, points: 3 },
      whatsapp_group: { label: "Added to Group", readyLabel: "Add to Group", icon: "fa-whatsapp", isBrand: true, points: 1 },
      whatsapp_broadcast: { label: "Broadcast Sent", readyLabel: "Send Broadcast", icon: "fa-whatsapp", isBrand: true, hasCount: true, points: 0.5 },
    }
  },
  other_social: {
    label: "Other Social",
    icon: "fa-share-alt",
    items: {
      twitter_follow: { label: "X/Twitter Follow", readyLabel: "Follow on X/Twitter", icon: "fa-twitter", isBrand: true, points: 0.5 },
      twitter_dm: { label: "X/Twitter DM", readyLabel: "Send X/Twitter DM", icon: "fa-twitter", isBrand: true, hasCount: true, points: 1 },
      twitter_reply: { label: "X/Twitter Reply", readyLabel: "Reply on X/Twitter", icon: "fa-twitter", isBrand: true, hasCount: true, points: 1 },
      twitter_retweet: { label: "Retweeted", readyLabel: "Retweet", icon: "fa-twitter", isBrand: true, hasCount: true, points: 0.5 },
      facebook_connect: { label: "Facebook Connect", readyLabel: "Connect on Facebook", icon: "fa-facebook", isBrand: true, points: 0.5 },
      facebook_message: { label: "Facebook Message", readyLabel: "Send Facebook Message", icon: "fa-facebook", isBrand: true, hasCount: true, points: 1 },
      tiktok_follow: { label: "TikTok Follow", readyLabel: "Follow on TikTok", icon: "fa-tiktok", isBrand: true, points: 0.5 },
      tiktok_comment: { label: "TikTok Comment", readyLabel: "Comment on TikTok", icon: "fa-tiktok", isBrand: true, hasCount: true, points: 1 },
      youtube_subscribe: { label: "YouTube Subscribe", readyLabel: "Subscribe on YouTube", icon: "fa-youtube", isBrand: true, points: 0.5 },
      youtube_comment: { label: "YouTube Comment", readyLabel: "Comment on YouTube", icon: "fa-youtube", isBrand: true, hasCount: true, points: 1 },
    }
  },
  content: {
    label: "Content & Events",
    icon: "fa-calendar-alt",
    items: {
      loom_video: { label: "Loom Video Sent", readyLabel: "Send Loom Video", icon: "fa-video", hasCount: true, points: 2 },
      video_message: { label: "Video Message Sent", readyLabel: "Send Video Message", icon: "fa-film", hasCount: true, points: 2 },
      voice_message: { label: "Voice Message Sent", readyLabel: "Send Voice Message", icon: "fa-microphone", hasCount: true, points: 1.5 },
      video_testimonial: { label: "Video Testimonial", readyLabel: "Request Video Testimonial", icon: "fa-film", points: 4 },
      webinar: { label: "Webinar Invite Sent", readyLabel: "Send Webinar Invite", icon: "fa-desktop", hasCount: true, points: 2 },
      webinar_attended: { label: "Webinar Attended", readyLabel: "Invite to Webinar", icon: "fa-desktop", hasCount: true, points: 2 },
      webinar_hosted: { label: "Hosted Webinar", readyLabel: "Host Webinar", icon: "fa-broadcast-tower", hasCount: true, points: 3 },
      workshop_invite: { label: "Workshop Invite Sent", readyLabel: "Send Workshop Invite", icon: "fa-chalkboard-teacher", hasCount: true, points: 2.5 },
      podcast_guest: { label: "Podcast Guest", readyLabel: "Invite to Podcast", icon: "fa-podcast", points: 4 },
      lead_magnet: { label: "Lead Magnet Sent", readyLabel: "Send Lead Magnet", icon: "fa-magnet", points: 1 },
      ebook_sent: { label: "Ebook Sent", readyLabel: "Send Ebook", icon: "fa-book", hasCount: true, points: 1.5 },
      whitepaper_sent: { label: "Whitepaper Sent", readyLabel: "Send Whitepaper", icon: "fa-file-pdf", hasCount: true, points: 1.5 },
      case_study: { label: "Case Study Shared", readyLabel: "Share Case Study", icon: "fa-briefcase", hasCount: true, points: 2 },
      case_study_sent: { label: "Case Study Sent", readyLabel: "Send Case Study", icon: "fa-chart-line", hasCount: true, points: 2 },
      free_trial: { label: "Free Trial Offered", readyLabel: "Offer Free Trial", icon: "fa-gift", points: 3 },
      discount_offer: { label: "Discount Offered", readyLabel: "Offer Discount", icon: "fa-percent", hasCount: true, points: 2 },
      workshop: { label: "Workshop Attended", readyLabel: "Invite to Workshop", icon: "fa-chalkboard-teacher", hasCount: true, points: 3 },
    }
  },
  gifts: {
    label: "Gifts & Appreciation",
    icon: "fa-gift",
    items: {
      gift_sent: { label: "Gift Sent", readyLabel: "Send Gift", icon: "fa-gift", hasCount: true, points: 3 },
      handwritten_note: { label: "Handwritten Note", readyLabel: "Write Handwritten Note", icon: "fa-pen-fancy", hasCount: true, points: 2 },
      birthday_wish: { label: "Birthday Wish", readyLabel: "Send Birthday Wish", icon: "fa-birthday-cake", hasCount: true, points: 1 },
      thank_you_card: { label: "Thank You Card", readyLabel: "Send Thank You Card", icon: "fa-heart", hasCount: true, points: 1.5 },
      referral_bonus: { label: "Referral Bonus", readyLabel: "Give Referral Bonus", icon: "fa-user-plus", hasCount: true, points: 2 },
      discount_offered: { label: "Discount Offered", readyLabel: "Offer Discount", icon: "fa-percent", hasCount: true, points: 1 },
    }
  },
  tasks: {
    label: "Tasks & Internal",
    icon: "fa-tasks",
    items: {
      follow_up: { label: "Follow Up Done", readyLabel: "Follow Up Task", icon: "fa-redo", hasCount: true, points: 1 },
      research_lead: { label: "Lead Researched", readyLabel: "Research Lead", icon: "fa-search", points: 1 },
      update_crm: { label: "CRM Updated", readyLabel: "Update CRM", icon: "fa-database", points: 0.5 },
      assign_to_rep: { label: "Assigned to Rep", readyLabel: "Assign to Rep", icon: "fa-user-plus", points: 1 },
      add_note: { label: "Note Added", readyLabel: "Add Note", icon: "fa-sticky-note", points: 0.5 },
      add_tag: { label: "Tag Added", readyLabel: "Add Tag", icon: "fa-tag", points: 0.25 },
      move_stage: { label: "Stage Moved", readyLabel: "Move Stage", icon: "fa-arrows-alt-h", points: 1 },
      create_task: { label: "Task Created", readyLabel: "Create Task", icon: "fa-check-square", points: 0.5 },
      slack_message: { label: "Slack Message Sent", readyLabel: "Send Slack Message", icon: "fa-slack", isBrand: true, hasCount: true, points: 1 },
    }
  },
};

// Flache Liste aller Touchpoints (für Kompatibilität)
const ALL_TOUCHPOINTS = Object.values(TOUCHPOINT_GROUPS).reduce((acc, group) => {
  return { ...acc, ...group.items };
}, {});

// Vordefinierte Tags für Leads (wie im FlowBuilderCanvas)
const PREDEFINED_TAGS = [
  { id: 'dream_client', name: 'Dream Client', color: '#8b5cf6', icon: 'fa-star' },
  { id: 'hot_lead', name: 'Hot Lead', color: '#ef4444', icon: 'fa-fire' },
  { id: 'warm_lead', name: 'Warm Lead', color: '#f59e0b', icon: 'fa-temperature-half' },
  { id: 'cold_lead', name: 'Cold Lead', color: '#3b82f6', icon: 'fa-snowflake' },
  { id: 'qualified', name: 'Qualified', color: '#22c55e', icon: 'fa-check-circle' },
  { id: 'not_qualified', name: 'Not Qualified', color: '#6b7280', icon: 'fa-times-circle' },
  { id: 'decision_maker', name: 'Decision Maker', color: '#ec4899', icon: 'fa-user-tie' },
  { id: 'influencer', name: 'Influencer', color: '#06b6d4', icon: 'fa-bullhorn' },
  { id: 'high_value', name: 'High Value', color: '#eab308', icon: 'fa-gem' },
  { id: 'high_budget', name: 'High Budget', color: '#22c55e', icon: 'fa-money-bill-wave' },
  { id: 'medium_budget', name: 'Medium Budget', color: '#f59e0b', icon: 'fa-money-bill' },
  { id: 'low_budget', name: 'Low Budget', color: '#ef4444', icon: 'fa-coins' },
  { id: 'priority', name: 'Priority', color: '#dc2626', icon: 'fa-exclamation-circle' },
  { id: 'follow_up_needed', name: 'Follow Up Needed', color: '#f97316', icon: 'fa-clock' },
  { id: 'nurture', name: 'Nurture', color: '#84cc16', icon: 'fa-seedling' },
  { id: 'engaged', name: 'Engaged', color: '#14b8a6', icon: 'fa-comments' },
  { id: 'unresponsive', name: 'Unresponsive', color: '#9ca3af', icon: 'fa-user-slash' },
  { id: 'interested', name: 'Interested', color: '#10b981', icon: 'fa-thumbs-up' },
  { id: 'not_interested', name: 'Not Interested', color: '#ef4444', icon: 'fa-thumbs-down' },
  { id: 'competitor', name: 'Competitor', color: '#7c3aed', icon: 'fa-chess' },
  { id: 'partner_potential', name: 'Partner Potential', color: '#0ea5e9', icon: 'fa-handshake' },
  { id: 'referral', name: 'Referral', color: '#d946ef', icon: 'fa-share' },
  { id: 'vip', name: 'VIP', color: '#fbbf24', icon: 'fa-crown' },
];

// Alle verfügbaren Felder für Leads
const ALL_FIELDS = {
  business_name: { label: "Business Name", icon: "fa-building" },
  lead_email: { label: "Email", icon: "fa-envelope" },
  lead_description: { label: "Description", icon: "fa-align-left" },
  lead_website: { label: "Website", icon: "fa-globe" },
  lead_instagram: { label: "Instagram", icon: "fa-instagram" },
  lead_tiktok: { label: "TikTok", icon: "fa-music" },
  lead_phone: { label: "Phone", icon: "fa-phone" },
  lead_address: { label: "Address", icon: "fa-map-marker-alt" },
  lead_notes: { label: "Notes", icon: "fa-sticky-note" },
  lead_decision_makers: { label: "Decision Makers", icon: "fa-users" },
  lead_company: { label: "Company", icon: "fa-briefcase" },
  lead_position: { label: "Position", icon: "fa-id-badge" },
  lead_linkedin: { label: "LinkedIn", icon: "fa-linkedin" },
  lead_twitter: { label: "Twitter/X", icon: "fa-twitter" },
  lead_facebook: { label: "Facebook", icon: "fa-facebook" },
  lead_youtube: { label: "YouTube", icon: "fa-youtube" },
  lead_referral_by: { label: "Referred By", icon: "fa-user-friends" },
};

// ========== HILFSFUNKTIONEN (außerhalb der Komponente) ==========

// Tage berechnen
const getDaysAgo = (timestamp) => {
  if (!timestamp) return null;
  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  if (isNaN(time)) return null;
  const days = Math.floor((Date.now() - time) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

// Datum formatieren
const formatDate = (timestamp) => {
  if (!timestamp) return null;
  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  if (isNaN(time)) return null;
  const date = new Date(time);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ========== LEAD DESCRIPTION / DECISION MAKERS (schöne Darstellung) ==========
// Die `lead_description` ist ein gestapelter Text-Blob: die Recherche-Pipeline
// hängt Blöcke mit Markern an ([Potential X/10], — Web Research —, [IG Research],
// [SEO]). Wir zerlegen ihn in benannte Sektionen und rendern ihn wie im
// Email-Modal (ResearchInfoTabs) — statt einer Textwand.

const DESCRIPTION_SECTION_META = {
  base: { label: "Overview", icon: "fa-align-left" },
  potential: { label: "Fit", icon: "fa-bullseye" },
  research: { label: "Web Research", icon: "fa-microscope" },
  instagram: { label: "Instagram", icon: "fa-instagram", brand: true },
  seo: { label: "SEO", icon: "fa-magnifying-glass-chart" },
};

// Max. Höhe der eingeklappten Description, bevor der "Show more"-Toggle kommt.
const DESCRIPTION_COLLAPSED_MAX = 280;

const fitTone = (score) => (score == null ? "" : score >= 6 ? "high" : score >= 3 ? "mid" : "low");

// Zerlegt den Blob anhand der Marker in { type, score, text }-Sektionen.
const parseLeadDescription = (raw) => {
  const text = (raw || "").toString().trim();
  if (!text) return [];

  const markerRe = /(\[Potential\s+(\d+)\s*\/\s*10\]|—\s*Web Research\s*—|\[IG Research\]|\[SEO\])/gi;
  const sections = [];
  let current = { type: "base", score: null, start: 0 };
  let match;

  const push = (endIdx) => {
    const body = text.slice(current.start, endIdx).trim();
    if (body) sections.push({ type: current.type, score: current.score, text: body });
  };

  // eslint-disable-next-line no-cond-assign
  while ((match = markerRe.exec(text)) !== null) {
    push(match.index);
    const token = match[1].toLowerCase();
    let type = "base";
    let score = null;
    if (token.startsWith("[potential")) { type = "potential"; score = parseInt(match[2], 10); }
    else if (token.includes("web research")) type = "research";
    else if (token.startsWith("[ig")) type = "instagram";
    else if (token.startsWith("[seo")) type = "seo";
    current = { type, score, start: markerRe.lastIndex };
  }
  push(text.length);

  // Nur ein einziger, unmarkierter Block → als schlichte Übersicht behandeln.
  return sections;
};

// Zerlegt den Text einer Sektion in Blöcke: Zwischenüberschriften ("Profil:"),
// Aufzählungen (•/-/*), und Fließtext. Kommt sowohl mit Zeilenumbrüchen als
// auch mit inline "•"-Bullets klar.
const parseDescriptionBlocks = (text) => {
  const lines = (text || "").split(/\r?\n/).map((l) => l.trim());
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) { blocks.push({ kind: "bullets", items: bullets }); bullets = []; }
  };

  lines.forEach((line) => {
    if (!line) { flush(); return; }

    // Inline-Bullets: "Profil: • a • b • c"
    const bulletIdx = line.indexOf("•");
    if (bulletIdx !== -1) {
      flush();
      const lead = line.slice(0, bulletIdx).trim();
      if (lead) {
        if (/[:：]$/.test(lead)) blocks.push({ kind: "subhead", label: lead.replace(/[:：]\s*$/, "") });
        else blocks.push({ kind: "text", text: lead });
      }
      line.slice(bulletIdx + 1).split("•").map((s) => s.trim()).filter(Boolean).forEach((it) => bullets.push(it));
      flush();
      return;
    }

    // Zeilen-Bullets: "- foo" / "* foo"
    const leadBullet = line.match(/^[-*]\s+(.*)$/);
    if (leadBullet) { bullets.push(leadBullet[1].trim()); return; }

    flush();

    // Kurze Zwischenüberschrift, die auf ":" endet (z.B. "Outreach-Hooks:")
    const heading = line.match(/^([\p{L}][\p{L} /&+-]{1,38}):\s*(.*)$/u);
    if (heading && !/^https?:/i.test(line)) {
      blocks.push({ kind: "subhead", label: heading[1].trim() });
      if (heading[2]) blocks.push({ kind: "text", text: heading[2].trim() });
      return;
    }

    blocks.push({ kind: "text", text: line });
  });
  flush();
  return blocks;
};

const DescriptionBlocks = ({ text }) => {
  const blocks = useMemo(() => parseDescriptionBlocks(text), [text]);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === "subhead") return <div key={i} className="lead-desc-subhead">{b.label}</div>;
        if (b.kind === "bullets") {
          return (
            <ul key={i} className="lead-desc-bullets">
              {b.items.map((it, j) => (
                <li key={j}><i className="fa fa-circle" /><span>{it}</span></li>
              ))}
            </ul>
          );
        }
        return <p key={i} className="lead-desc-text">{b.text}</p>;
      })}
    </>
  );
};

const LeadDescriptionView = ({ text, onEdit }) => {
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const sections = useMemo(() => parseLeadDescription(text), [text]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > DESCRIPTION_COLLAPSED_MAX + 8);
  }, [sections, expanded]);

  if (sections.length === 0) return null;

  // Reiner, unmarkierter Text braucht keinen redundanten "Overview"-Header.
  const plainOnly = sections.length === 1 && sections[0].type === "base";

  return (
    <div className="lead-desc">
      <div
        ref={bodyRef}
        className={`lead-desc-body ${!expanded && overflowing ? "collapsed" : ""}`}
      >
        {plainOnly ? (
          <div className="lead-desc-section lead-desc-section--plain">
            <DescriptionBlocks text={sections[0].text} />
          </div>
        ) : (
          sections.map((s, i) => {
            const meta = DESCRIPTION_SECTION_META[s.type] || DESCRIPTION_SECTION_META.base;
            const tier = s.type === "potential" ? fitTone(s.score) : null;
            return (
              <section key={i} className="lead-desc-section">
                <div className="lead-desc-secHead">
                  <i className={`${meta.brand ? "fab" : "fa"} ${meta.icon}`} />
                  <span>{meta.label}</span>
                  {s.score != null && (
                    <span className={`lead-desc-score lead-desc-score--${tier}`}>{s.score}/10</span>
                  )}
                </div>
                <DescriptionBlocks text={s.text} />
              </section>
            );
          })
        )}
        {!expanded && overflowing && <div className="lead-desc-fade" />}
      </div>

      <div className="lead-desc-actions">
        {overflowing && (
          <button type="button" className="lead-desc-toggle" onClick={() => setExpanded((v) => !v)}>
            <i className={`fa fa-chevron-${expanded ? "up" : "down"}`} />
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
        {onEdit && (
          <button type="button" className="lead-desc-edit" onClick={onEdit}>
            <i className="fa fa-pen" /> Edit
          </button>
        )}
      </div>
    </div>
  );
};

const DM_SOCIAL_ICONS = {
  linkedin: "fab fa-linkedin",
  facebook: "fab fa-facebook",
  instagram: "fab fa-instagram",
  x: "fab fa-x-twitter",
  twitter: "fab fa-x-twitter",
  xing: "fab fa-xing",
};

const buildDmSocialHref = (platform, val) => {
  if (!val || !String(val).trim()) return null;
  const v = String(val).trim();
  if (v.startsWith("http")) return v;
  const domains = {
    linkedin: "linkedin.com/in", facebook: "facebook.com", instagram: "instagram.com",
    x: "x.com", twitter: "x.com", xing: "xing.com/profile",
  };
  return domains[platform] ? `https://${domains[platform]}/${v.replace("@", "")}` : `https://${v}`;
};

const parseDecisionMakers = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(parsed)
      ? parsed.filter((dm) => dm && (dm.name || dm.role || dm.position || dm.email))
      : [];
  } catch (e) {
    return [];
  }
};

const LeadDecisionMakersView = ({ raw }) => {
  const dms = useMemo(() => parseDecisionMakers(raw), [raw]);
  if (dms.length === 0) return null;
  return (
    <div className="lead-dm-list">
      {dms.map((dm, idx) => {
        const socials = dm.socials && typeof dm.socials === "object" ? dm.socials : {};
        const initial = (dm.name || dm.role || "?").toString().trim().charAt(0).toUpperCase();
        return (
          <div key={idx} className="lead-dm-card">
            <div className="lead-dm-avatar">{initial || <i className="fa fa-user-tie" />}</div>
            <div className="lead-dm-info">
              <span className="lead-dm-name">{dm.name || "Unknown"}</span>
              {(dm.role || dm.position) && (
                <span className="lead-dm-role">{dm.role || dm.position}</span>
              )}
              {dm.email && (
                <a href={`mailto:${dm.email}`} className="lead-dm-contact" onClick={(e) => e.stopPropagation()}>
                  <i className="fa fa-envelope" />{dm.email}
                </a>
              )}
              {dm.phone && (
                <a href={`tel:${dm.phone}`} className="lead-dm-contact" onClick={(e) => e.stopPropagation()}>
                  <i className="fa fa-phone" />{dm.phone}
                </a>
              )}
            </div>
            <div className="lead-dm-socials">
              {Object.entries(socials).map(([platform, url]) => {
                const href = buildDmSocialHref(platform, url);
                const icon = DM_SOCIAL_ICONS[platform];
                if (!href || !icon) return null;
                return (
                  <a key={platform} href={href} target="_blank" rel="noopener noreferrer"
                    className="lead-dm-social" title={platform} onClick={(e) => e.stopPropagation()}>
                    <i className={icon} />
                  </a>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ========== LEAD DETAIL MODAL (außerhalb der Komponente für stabile Referenz) ==========

const LeadDetailModal = ({ lead, onClose, onSaveField, isNew = false, onCreateLead, allLeads = [], onSelectLead }) => {
  const modalRef = useRef(null);
  const [showAddField, setShowAddField] = useState(false);
  const [showAddTouchpoint, setShowAddTouchpoint] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Team-System: Hole die aktive World-ID für File-Uploads
  const { userId } = useActiveWorld();
  
  // Scroll to top when lead changes
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [lead?.id]);
  
  // State für neuen Lead
  const [newLeadData, setNewLeadData] = useState({
    business_name: "",
    lead_email: "",
    lead_description: "",
    lead_website: "",
    lead_instagram: "",
    lead_phone: "",
    lead_phone_whatsapp: false,
    lead_notes: "",
    lead_referral_by: null,
  });
  const [creatingLead, setCreatingLead] = useState(false);
  
  // File Upload States
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Avatar/Profile Image Upload States
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Akzeptierte Dateitypen
  const acceptedFileTypes = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx";
  
  // Datei hochladen
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !lead?.id) return;
    
    setUploadingFile(true);
    // Verwende die userId aus dem äußeren Scope (activeWorldUserId)
    
    try {
      const uploadedFiles = [...(currentLead.files || [])];
      
      for (const file of files) {
        // Datei-Größe prüfen (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`File "${file.name}" is too large. Max 10MB allowed.`);
          continue;
        }
        
        // Dateityp bestimmen
        const fileType = file.type.startsWith('image/') ? 'image' 
          : file.type === 'application/pdf' ? 'pdf' 
          : 'document';
        
        // Upload zu Firebase Storage
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const fileRef = storageRef(storage, `World/${userId}/leads/${lead.id}/files/${fileName}`);
        
        await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(fileRef);
        
        uploadedFiles.push({
          id: `file_${timestamp}`,
          name: file.name,
          type: fileType,
          mimeType: file.type,
          size: file.size,
          url: downloadUrl,
          storagePath: `World/${userId}/leads/${lead.id}/files/${fileName}`,
          uploadedAt: timestamp
        });
      }
      
      // Speichere in Firestore
      await onSaveField(lead.id, 'files', uploadedFiles);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Datei löschen
  const handleDeleteFile = async (fileToDelete) => {
    if (!lead?.id || !fileToDelete) return;
    
    const confirmed = window.confirm(`Delete "${fileToDelete.name}"?`);
    if (!confirmed) return;
    
    try {
      // Aus Storage löschen
      if (fileToDelete.storagePath) {
        const fileRef = storageRef(storage, fileToDelete.storagePath);
        await deleteObject(fileRef).catch(() => {}); // Ignoriere Fehler wenn Datei nicht existiert
      }
      
      // Aus Firestore entfernen
      const updatedFiles = (currentLead.files || []).filter(f => f.id !== fileToDelete.id);
      await onSaveField(lead.id, 'files', updatedFiles);
      
      // Schließe Preview wenn diese Datei angezeigt wird
      if (previewFile?.id === fileToDelete.id) {
        setPreviewFile(null);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file. Please try again.');
    }
  };
  
  // Profilbild hochladen
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !lead?.id) return;
    
    // Nur Bilder erlauben
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF, WebP)');
      return;
    }
    
    // Max 5MB für Avatar
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large. Max 5MB allowed.');
      return;
    }
    
    setUploadingAvatar(true);
    // Verwende die userId aus dem äußeren Scope (activeWorldUserId)
    
    try {
      // Altes Profilbild löschen wenn vorhanden
      if (currentLead.profile_image?.storagePath) {
        const oldRef = storageRef(storage, currentLead.profile_image.storagePath);
        await deleteObject(oldRef).catch(() => {});
      }
      
      // Neues Bild hochladen
      const timestamp = Date.now();
      const extension = file.name.split('.').pop();
      const fileName = `profile_${timestamp}.${extension}`;
      const fileRef = storageRef(storage, `World/${userId}/leads/${lead.id}/profile/${fileName}`);
      
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      
      // Speichere in Firestore
      await onSaveField(lead.id, 'profile_image', {
        url: downloadUrl,
        storagePath: `World/${userId}/leads/${lead.id}/profile/${fileName}`,
        uploadedAt: timestamp
      });
      
    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };
  
  // Profilbild löschen
  const handleDeleteAvatar = async () => {
    if (!lead?.id || !currentLead.profile_image) return;
    
    const confirmed = window.confirm('Remove profile picture?');
    if (!confirmed) return;
    
    try {
      if (currentLead.profile_image.storagePath) {
        const fileRef = storageRef(storage, currentLead.profile_image.storagePath);
        await deleteObject(fileRef).catch(() => {});
      }
      await onSaveField(lead.id, 'profile_image', null);
    } catch (error) {
      console.error('Error deleting profile image:', error);
    }
  };
  
  // Dateigröße formatieren
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Referral Search State
  const [referralSearch, setReferralSearch] = useState("");
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  
  // Gefilterte Leads für Referral-Suche (ohne den aktuellen Lead)
  const filteredReferralLeads = allLeads.filter(l => {
    if (!referralSearch.trim()) return false;
    if (l.id === lead?.id) return false; // Nicht sich selbst referenzieren
    const searchLower = referralSearch.toLowerCase();
    const name = l.business_name?.toLowerCase() || "";
    let dmSearch = '';
    try {
      if (l.lead_decision_makers) {
        const dms = typeof l.lead_decision_makers === 'string' ? JSON.parse(l.lead_decision_makers) : l.lead_decision_makers;
        dmSearch = dms.map(dm => dm.name).filter(Boolean).join(' ').toLowerCase();
      }
    } catch (e) {}
    return name.includes(searchLower) || dmSearch.includes(searchLower);
  }).slice(0, 5); // Max 5 Ergebnisse

  // Referrer-Lead finden
  const getReferrerLead = (referralId) => {
    if (!referralId) return null;
    return allLeads.find(l => l.id === referralId);
  };

  // Leads finden, die von diesem Lead empfohlen wurden
  const getReferredLeads = () => {
    if (isNew || !lead?.id) return [];
    return allLeads.filter(l => l.lead_referral_by === lead.id);
  };
  
  const referredLeads = getReferredLeads();
  const isPlatinumMember = referredLeads.length >= 6;
  const isGoldMember = referredLeads.length >= 3 && referredLeads.length < 6;

  // Aktuelle Lead-Daten (entweder bestehend oder neu)
  const currentLead = isNew ? newLeadData : lead;

  // Notes sind eine Liste von Note-Objekten (gleiche Form wie im Notes-Modal
  // der Tabelle). Alte Leads können hier noch einen Freitext-String haben.
  const leadNotes = normalizeLeadNotes(currentLead.lead_notes);

  // Felder die noch nicht ausgefüllt sind (ohne lead_notes, da es separat angezeigt wird)
  const emptyFields = Object.keys(ALL_FIELDS).filter(
    (key) => key !== "lead_notes" && (!currentLead[key] || currentLead[key].toString().trim() === "")
  );

  const handleAddField = (fieldKey) => {
    setEditingField(fieldKey);
    setEditValue("");
    setShowAddField(false);
  };

  const handleSave = async (fieldKey, value) => {
    if (isNew) {
      // Bei neuem Lead: lokalen State aktualisieren
      setNewLeadData(prev => ({ ...prev, [fieldKey]: value }));
      setEditingField(null);
      setEditValue("");
      return;
    }
    
    setSaving(true);
    const success = await onSaveField(lead.id, fieldKey, value);
    setSaving(false);
    if (success) {
      setEditingField(null);
      setEditValue("");
    }
  };

  // Notes werden angehängt, nie überschrieben — sonst gehen die Notizen aus
  // Notes-Modal, Flows und LeadSwipe verloren.
  const handleAddNote = async () => {
    const text = editValue.trim();
    if (!text) return;
    await handleSave("lead_notes", [...leadNotes, createPrivateNote({ activity: "note", text })]);
  };

  const handleDeleteNote = async (index) => {
    const next = leadNotes.filter((_, i) => i !== index);
    setSaving(true);
    await onSaveField(lead.id, "lead_notes", next);
    setSaving(false);
  };

  const handleCreateLead = async () => {
    if (!newLeadData.business_name.trim()) {
      return; // Business name ist Pflicht
    }
    
    setCreatingLead(true);
    const success = await onCreateLead(newLeadData);
    setCreatingLead(false);
    
    if (success) {
      onClose();
    }
  };

  // Bei neuem Lead: Feld direkt im State aktualisieren
  const handleNewLeadFieldChange = (fieldKey, value) => {
    setNewLeadData(prev => ({ ...prev, [fieldKey]: value }));
  };

  return (
    <div className="lead-modal-overlay" onClick={onClose}>
      <div ref={modalRef} className={`lead-modal ${isNew ? 'lead-modal-new' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="lead-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="lead-modal-header">
          <div 
            className={`lead-modal-avatar ${isNew ? 'new-lead-avatar' : ''} ${isPlatinumMember ? 'platinum-modal-avatar' : isGoldMember ? 'gold-modal-avatar' : ''} ${currentLead.profile_image?.url ? 'has-image' : ''} ${!isNew ? 'clickable' : ''}`}
            onClick={() => !isNew && avatarInputRef.current?.click()}
            title={!isNew ? (currentLead.profile_image?.url ? 'Click to change profile picture' : 'Click to upload profile picture') : ''}
          >
            {isNew ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            ) : uploadingAvatar ? (
              <i className="fa fa-spinner fa-spin"></i>
            ) : currentLead.profile_image?.url ? (
              <img src={currentLead.profile_image.url} alt={currentLead.business_name || 'Profile'} />
            ) : (
              currentLead.business_name?.charAt(0)?.toUpperCase() || "?"
            )}
            {!isNew && !uploadingAvatar && (
              <div className="avatar-upload-overlay">
                <i className="fa fa-camera"></i>
              </div>
            )}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
          </div>
          {!isNew && currentLead.profile_image?.url && (
            <button 
              className="avatar-delete-btn" 
              onClick={(e) => { e.stopPropagation(); handleDeleteAvatar(); }}
              title="Remove profile picture"
            >
              <i className="fa fa-times"></i>
            </button>
          )}
          <div className="lead-modal-title">
            {isNew ? (
              <input
                type="text"
                className="lead-modal-name-input"
                placeholder="Enter business name..."
                value={newLeadData.business_name}
                onChange={(e) => handleNewLeadFieldChange('business_name', e.target.value)}
                autoFocus
              />
            ) : editingField === 'business_name' ? (
              <div className="lead-modal-name-edit">
                <input
                  type="text"
                  className="lead-modal-name-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editValue.trim()) {
                      handleSave('business_name', editValue);
                    } else if (e.key === 'Escape') {
                      setEditingField(null);
                      setEditValue('');
                    }
                  }}
                  autoFocus
                />
                <div className="lead-modal-name-actions">
                  <button 
                    className="name-save-btn"
                    onClick={() => handleSave('business_name', editValue)}
                    disabled={!editValue.trim() || saving}
                  >
                    <i className="fa fa-check"></i>
                  </button>
                  <button 
                    className="name-cancel-btn"
                    onClick={() => {
                      setEditingField(null);
                      setEditValue('');
                    }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              </div>
            ) : (
              <h2 
                className="lead-modal-name-editable"
                onClick={() => {
                  setEditingField('business_name');
                  setEditValue(currentLead.business_name || '');
                }}
                title="Click to edit"
              >
                {currentLead.business_name || "Unknown"}
                <i className="fa fa-pen lead-modal-name-edit-icon"></i>
                {isPlatinumMember && <span className="platinum-member-tag-modal">Platinum Connect</span>}
                {isGoldMember && <span className="gold-member-tag-modal">Gold Giver</span>}
              </h2>
            )}
            {!isNew && (
              <div className="lead-modal-dates">
                {isPlatinumMember && (
                  <span className="lead-modal-date platinum-referrals-modal">
                    <i className="fa fa-gem"></i>
                    {referredLeads.length} Referrals
                  </span>
                )}
                {isGoldMember && (
                  <span className="lead-modal-date gold-referrals-modal">
                    <i className="fa fa-crown"></i>
                    {referredLeads.length} Referrals
                  </span>
                )}
                {currentLead.lead_last_email?.timestamp && (
                  <span className="lead-modal-date">
                    <i className="fa fa-paper-plane"></i>
                    Last Contacted: {formatDate(currentLead.lead_last_email.timestamp)} ({getDaysAgo(currentLead.lead_last_email.timestamp)})
                  </span>
                )}
                {currentLead.lead_since && (
                  <span className="lead-modal-date lead-since">
                    <i className="fa fa-star"></i>
                    Lead Since: {formatDate(currentLead.lead_since)} ({getDaysAgo(currentLead.lead_since)})
                  </span>
                )}
              </div>
            )}
            {isNew && (
              <p className="lead-modal-subtitle">Add a new lead to your list</p>
            )}
          </div>
        </div>

        <div className="lead-modal-content">
          {/* Quick Input Fields for New Lead */}
          {isNew && (
            <div className="new-lead-quick-fields">
              <div className="new-lead-field">
                <label>
                  <i className="fa fa-envelope"></i>
                  Email
                </label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={newLeadData.lead_email}
                  onChange={(e) => handleNewLeadFieldChange('lead_email', e.target.value)}
                />
              </div>
              
              <div className="new-lead-field">
                <label>
                  <i className="fa fa-globe"></i>
                  Website
                </label>
                <input
                  type="text"
                  placeholder="www.example.com"
                  value={newLeadData.lead_website}
                  onChange={(e) => handleNewLeadFieldChange('lead_website', e.target.value)}
                />
              </div>
              
              <div className="new-lead-field">
                <label>
                  <i className="fa fa-phone"></i>
                  Phone
                </label>
                <div className="phone-input-wrapper">
                  <input
                    type="tel"
                    placeholder="+1 234 567 890"
                    value={newLeadData.lead_phone}
                    onChange={(e) => handleNewLeadFieldChange('lead_phone', e.target.value)}
                  />
                  <button 
                    type="button"
                    className={`whatsapp-toggle ${newLeadData.lead_phone_whatsapp ? 'active' : ''}`}
                    onClick={() => handleNewLeadFieldChange('lead_phone_whatsapp', !newLeadData.lead_phone_whatsapp)}
                    title={newLeadData.lead_phone_whatsapp ? 'WhatsApp enabled' : 'Enable WhatsApp'}
                  >
                    <i className="fab fa-whatsapp"></i>
                  </button>
                </div>
              </div>
              
              <div className="new-lead-field">
                <label>
                  <i className="fab fa-instagram"></i>
                  Instagram
                </label>
                <input
                  type="text"
                  placeholder="@username"
                  value={newLeadData.lead_instagram}
                  onChange={(e) => handleNewLeadFieldChange('lead_instagram', e.target.value)}
                />
              </div>
              
              <div className="new-lead-field full-width">
                <label>
                  <i className="fa fa-align-left"></i>
                  Description
                </label>
                <textarea
                  placeholder="Brief description of this lead..."
                  value={newLeadData.lead_description}
                  onChange={(e) => handleNewLeadFieldChange('lead_description', e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="new-lead-field full-width">
                <label>
                  <i className="fa fa-sticky-note"></i>
                  Notes
                </label>
                <textarea
                  placeholder="Add notes, reminders, or any additional info..."
                  value={newLeadData.lead_notes}
                  onChange={(e) => handleNewLeadFieldChange('lead_notes', e.target.value)}
                  rows={3}
                />
              </div>
              
              {/* Referral By Field */}
              <div className="new-lead-field full-width">
                <label>
                  <i className="fa fa-user-friends"></i>
                  Referred By
                </label>
                <div className="referral-search-wrapper">
                  {newLeadData.lead_referral_by ? (
                    <div className="referral-selected">
                      <div className="referral-selected-info">
                        <div className="referral-selected-avatar">
                          {getReferrerLead(newLeadData.lead_referral_by)?.business_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <span>{getReferrerLead(newLeadData.lead_referral_by)?.business_name || "Unknown"}</span>
                      </div>
                      <button 
                        type="button"
                        className="referral-remove-btn"
                        onClick={() => handleNewLeadFieldChange('lead_referral_by', null)}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Search leads to link as referrer..."
                        value={referralSearch}
                        onChange={(e) => {
                          setReferralSearch(e.target.value);
                          setShowReferralDropdown(true);
                        }}
                        onFocus={() => setShowReferralDropdown(true)}
                      />
                      {showReferralDropdown && filteredReferralLeads.length > 0 && (
                        <div className="referral-dropdown">
                          {filteredReferralLeads.map(refLead => (
                            <button
                              key={refLead.id}
                              type="button"
                              className="referral-dropdown-item"
                              onClick={() => {
                                handleNewLeadFieldChange('lead_referral_by', refLead.id);
                                setReferralSearch("");
                                setShowReferralDropdown(false);
                              }}
                            >
                              <div className="referral-dropdown-avatar">
                                {refLead.business_name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <div className="referral-dropdown-info">
                                <span className="referral-dropdown-name">{refLead.business_name}</span>
                                {refLead.lead_email && (
                                  <span className="referral-dropdown-email">{refLead.lead_email}</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 2-Column Layout for existing leads */}
          {!isNew && (
            <div className="lead-modal-content-grid">
              {/* LEFT COLUMN: Notes & Touchpoints */}
              <div className="lead-modal-column lead-modal-column-left">
                {/* Notes Section */}
                <div className="lead-modal-notes-section">
                  <div className="lead-modal-notes-header">
                    <i className="fa fa-sticky-note"></i>
                    <span>Notes</span>
                    {leadNotes.length > 0 && (
                      <span className="lead-modal-notes-count">{leadNotes.length}</span>
                    )}
                  </div>

                  {leadNotes.length > 0 && (
                    <div className="lead-modal-notes-list">
                      {[...leadNotes]
                        .map((note, index) => ({ note, index }))
                        .sort((a, b) => (b.note.createdAt || 0) - (a.note.createdAt || 0))
                        .map(({ note, index }) => {
                          const activityCfg = getActivityConfig(note.activity || "note");
                          const outcomeCfg = getOutcomeConfig(note.outcome);
                          const sentimentCfg = getSentimentConfig(note.sentiment);
                          const isOutreach = note.type === "outreach";
                          return (
                            <div key={note.id || index} className="lead-modal-note">
                              <div className="lead-modal-note-meta">
                                {isOutreach ? (
                                  <span className="lead-modal-note-badge lead-modal-note-badge--outreach">
                                    <i className="fa fa-paper-plane"></i> Outreach
                                  </span>
                                ) : (
                                  <span className={`lead-modal-note-badge lead-modal-note-badge--${activityCfg.id}`}>
                                    <i className={`fa ${activityCfg.icon}`}></i> {activityCfg.label}
                                  </span>
                                )}
                                {outcomeCfg && (
                                  <span className={`lead-modal-note-badge lead-modal-note-badge--${outcomeCfg.tone || "neutral"}`}>
                                    <i className={`fa ${outcomeCfg.icon}`}></i> {outcomeCfg.label}
                                  </span>
                                )}
                                {sentimentCfg && (
                                  <span className={`lead-modal-note-badge lead-modal-note-badge--sentiment-${sentimentCfg.id}`}>
                                    <i className={`fa ${sentimentCfg.icon}`}></i> {sentimentCfg.label}
                                  </span>
                                )}
                                {note.createdAt && (
                                  <span className="lead-modal-note-date">{formatDate(note.createdAt)}</span>
                                )}
                                <button
                                  type="button"
                                  className="lead-modal-note-delete"
                                  title="Delete note"
                                  disabled={saving}
                                  onClick={() => handleDeleteNote(index)}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </div>
                              {note.text && <p className="lead-modal-note-text">{note.text}</p>}
                              {note.createdByEmail && (
                                <span className="lead-modal-note-author">{note.createdByEmail}</span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {editingField === "lead_notes" ? (
                    <div className="lead-modal-notes-edit">
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="Add your notes here... (e.g., follow-up reminders, meeting notes, special requests)"
                        autoFocus
                        rows={4}
                      />
                      <div className="lead-modal-notes-actions">
                        <button
                          className="field-save-btn"
                          onClick={handleAddNote}
                          disabled={saving || !editValue.trim()}
                        >
                          {saving ? "Saving..." : "Save Note"}
                        </button>
                        <button
                          className="field-cancel-btn"
                          onClick={() => {
                            setEditingField(null);
                            setEditValue("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={`lead-modal-notes-content ${leadNotes.length === 0 ? 'empty' : 'compact'}`}
                      onClick={() => {
                        setEditingField("lead_notes");
                        setEditValue("");
                      }}
                    >
                      <p className="lead-modal-notes-placeholder">
                        <i className="fa fa-plus"></i>
                        {leadNotes.length > 0 ? "Add another note..." : "Click to add notes..."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Touchpoints Section */}
                <div className="lead-modal-touchpoints-section">
                  <div className="lead-modal-touchpoints-header">
                    <i className="fa fa-hand-pointer"></i>
                    <span>Touchpoints</span>
                    {currentLead.touchpoints && Object.keys(currentLead.touchpoints).length > 0 && (
                      <span className="touchpoints-count">
                        {Object.keys(currentLead.touchpoints).length}
                      </span>
                    )}
                  </div>

                  {/* Existing Touchpoints */}
                  {currentLead.touchpoints && Object.keys(currentLead.touchpoints).length > 0 && (
                    <div className="touchpoints-list">
                      {Object.entries(currentLead.touchpoints).map(([key, value]) => {
                        const touchpoint = ALL_TOUCHPOINTS[key];
                        if (!touchpoint) return null;
                        
                        return (
                          <div key={key} className="touchpoint-item">
                            <div className="touchpoint-info">
                              <i className={`${touchpoint.isBrand ? 'fab' : 'fa'} ${touchpoint.icon}`}></i>
                              <span>{touchpoint.label}</span>
                            </div>
                            {touchpoint.hasCount ? (
                              <div className="touchpoint-counter">
                                <button 
                                  className="touchpoint-counter-btn"
                                  onClick={() => {
                                    const newCount = Math.max(0, (value || 1) - 1);
                                    if (newCount === 0) {
                                      const newTouchpoints = { ...currentLead.touchpoints };
                                      delete newTouchpoints[key];
                                      onSaveField(lead.id, 'touchpoints', newTouchpoints);
                                    } else {
                                      onSaveField(lead.id, 'touchpoints', { ...currentLead.touchpoints, [key]: newCount });
                                    }
                                  }}
                                >
                                  <i className="fa fa-minus"></i>
                                </button>
                                <span className="touchpoint-count">{value || 1}</span>
                                <button 
                                  className="touchpoint-counter-btn"
                                  onClick={() => {
                                    onSaveField(lead.id, 'touchpoints', { ...currentLead.touchpoints, [key]: (value || 1) + 1 });
                                  }}
                                >
                                  <i className="fa fa-plus"></i>
                                </button>
                              </div>
                            ) : (
                              <button 
                                className="touchpoint-remove-btn"
                                onClick={() => {
                                  const newTouchpoints = { ...currentLead.touchpoints };
                                  delete newTouchpoints[key];
                                  onSaveField(lead.id, 'touchpoints', newTouchpoints);
                                }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add Touchpoint Button */}
                  <div className="lead-modal-add-touchpoint">
                    {showAddTouchpoint ? (
                      <div className="add-touchpoint-dropdown">
                        <div className="add-touchpoint-header">
                          <span>Add Touchpoint</span>
                          <button onClick={() => setShowAddTouchpoint(false)}>×</button>
                        </div>
                        <div className="add-touchpoint-list">
                          {Object.entries(TOUCHPOINT_GROUPS).map(([groupKey, group]) => {
                            // Filter items die noch nicht hinzugefügt wurden
                            const availableItems = Object.entries(group.items)
                              .filter(([key]) => !currentLead.touchpoints?.[key]);
                            
                            // Skip Gruppe wenn keine Items verfügbar
                            if (availableItems.length === 0) return null;
                            
                            return (
                              <div key={groupKey} className="add-touchpoint-group">
                                <div className="add-touchpoint-group-header">
                                  <i className={`${group.isBrand ? 'fab' : 'fa'} ${group.icon}`}></i>
                                  <span>{group.label}</span>
                                </div>
                                {availableItems.map(([key, tp]) => (
                                  <button
                                    key={key}
                                    className="add-touchpoint-option"
                                    onClick={() => {
                                      const newTouchpoints = { 
                                        ...(currentLead.touchpoints || {}), 
                                        [key]: tp.hasCount ? 1 : true 
                                      };
                                      onSaveField(lead.id, 'touchpoints', newTouchpoints);
                                      setShowAddTouchpoint(false);
                                    }}
                                  >
                                    <i className={`${tp.isBrand ? 'fab' : 'fa'} ${tp.icon}`}></i>
                                    <span>{tp.label}</span>
                                    {tp.hasCount && <span className="touchpoint-hint">counter</span>}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="add-touchpoint-btn"
                        onClick={() => setShowAddTouchpoint(true)}
                      >
                        <i className="fa fa-plus"></i>
                        Add Touchpoint
                      </button>
                    )}
                  </div>

                  {/* Touchpoint Barometer */}
                  {(() => {
                    // Count manual touchpoints with weighted points
                    const manualPoints = currentLead.touchpoints 
                      ? Object.entries(currentLead.touchpoints).reduce((sum, [key, value]) => {
                          const tp = ALL_TOUCHPOINTS[key];
                          if (!tp) return sum;
                          const pointValue = tp.points || 1;
                          const count = tp.hasCount ? (value || 1) : 1;
                          return sum + (pointValue * count);
                        }, 0)
                      : 0;
                    
                    // Count emails - check emailHistory first, fallback to lead_last_email
                    // Emails are worth 1 point each
                    let emailCount = 0;
                    if (currentLead.emailHistory?.length > 0) {
                      emailCount = currentLead.emailHistory.length;
                    } else if (currentLead.lead_last_email?.timestamp) {
                      emailCount = 1;
                    }
                    
                    const totalPoints = manualPoints + emailCount;
                    const maxPoints = 20;
                    const progress = Math.min(totalPoints, maxPoints);
                    const percentage = (progress / maxPoints) * 100;
                    
                    return (
                      <div className="touchpoint-barometer">
                        <div className="touchpoint-barometer-header">
                          <span className="touchpoint-barometer-label">
                            {progress >= 20 ? '🔥 Hot Lead' : progress >= 14 ? '🌡️ Warming Up' : progress >= 8 ? '👋 Getting There' : '❄️ Cold Lead'}
                          </span>
                          <span className={`touchpoint-barometer-value ${progress >= 20 ? 'complete' : ''}`}>
                            {Math.round(totalPoints * 10) / 10}<span className="of-ten">/20</span>
                          </span>
                        </div>
                        <div className="touchpoint-barometer-track">
                          <div 
                            className="touchpoint-barometer-fill"
                            style={{ 
                              width: `${percentage}%`,
                              background: progress >= 20 
                                ? 'linear-gradient(90deg, #f97316, #ef4444, #dc2626)' 
                                : progress >= 14 
                                  ? 'linear-gradient(90deg, #fbbf24, #f97316)' 
                                  : progress >= 8 
                                    ? 'linear-gradient(90deg, #a78bfa, #7c3aed)' 
                                    : 'linear-gradient(90deg, #cbd5e1, #94a3b8)'
                            }}
                          />
                        </div>
                        {emailCount > 0 && (
                          <div className="touchpoint-barometer-emails">
                            <i className="fa fa-paper-plane"></i>
                            <span>
                              {currentLead.emailHistory?.length > 0 
                                ? `${emailCount} email${emailCount > 1 ? 's' : ''} sent`
                                : 'Email sent'
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Tags Section */}
                <div className="lead-modal-tags-section">
                  <div className="lead-modal-tags-header">
                    <i className="fa fa-tags"></i>
                    <span>Tags</span>
                    {currentLead.tags && Object.keys(currentLead.tags).length > 0 && (
                      <span className="tags-count">
                        {Object.keys(currentLead.tags).length}
                      </span>
                    )}
                  </div>

                  {/* Existing Tags */}
                  {currentLead.tags && Object.keys(currentLead.tags).length > 0 && (
                    <div className="tags-list">
                      {Object.entries(currentLead.tags).map(([key, tag]) => (
                        <div 
                          key={key} 
                          className="tag-item"
                          style={{ backgroundColor: `${tag.color}15`, borderColor: tag.color }}
                        >
                          <i className={`fa ${tag.icon || 'fa-tag'}`} style={{ color: tag.color }}></i>
                          <span className="tag-name">{tag.name}</span>
                          {tag.addedBy === 'flow' && (
                            <span className="tag-source" title={`Added by flow: ${tag.flowName || 'Unknown'}`}>
                              <i className="fa fa-project-diagram"></i>
                            </span>
                          )}
                          <button 
                            className="tag-remove-btn"
                            onClick={() => {
                              const newTags = { ...currentLead.tags };
                              delete newTags[key];
                              onSaveField(lead.id, 'tags', newTags);
                            }}
                            title="Remove tag"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Tag UI */}
                  <div className="add-tag-container">
                    {showAddTag ? (
                      <div className="add-tag-dropdown">
                        <div className="add-tag-dropdown-header">
                          <span>Select a tag to add</span>
                          <button onClick={() => setShowAddTag(false)}>
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                        <div className="add-tag-grid">
                          {PREDEFINED_TAGS
                            .filter(tag => !currentLead.tags?.[tag.id])
                            .map((tag) => (
                              <button
                                key={tag.id}
                                className="add-tag-option"
                                onClick={() => {
                                  const newTags = {
                                    ...(currentLead.tags || {}), 
                                    [tag.id]: {
                                      id: tag.id,
                                      name: tag.name,
                                      color: tag.color,
                                      icon: tag.icon,
                                      addedAt: Date.now(),
                                      addedBy: 'manual'
                                    }
                                  };
                                  onSaveField(lead.id, 'tags', newTags);
                                  setShowAddTag(false);
                                }}
                                style={{ 
                                  '--tag-color': tag.color,
                                  borderColor: tag.color,
                                  backgroundColor: `${tag.color}10`
                                }}
                              >
                                <i className={`fa ${tag.icon}`} style={{ color: tag.color }}></i>
                                <span>{tag.name}</span>
                              </button>
                            ))}
                        </div>
                        {PREDEFINED_TAGS.filter(tag => !currentLead.tags?.[tag.id]).length === 0 && (
                          <div className="add-tag-empty">
                            <i className="fa fa-check-circle"></i>
                            <span>All tags have been added</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button 
                        className="add-tag-btn"
                        onClick={() => setShowAddTag(true)}
                      >
                        <i className="fa fa-plus"></i>
                        Add Tag
                      </button>
                    )}
                  </div>
                </div>

                {/* Files Section */}
                {!isNew && (
                  <div className="lead-modal-files-section">
                    <div className="lead-modal-files-header">
                      <i className="fa fa-folder-open"></i>
                      <span>Files</span>
                      {currentLead.files?.length > 0 && (
                        <span className="files-count">{currentLead.files.length}</span>
                      )}
                    </div>
                    
                    {/* File Upload Button */}
                    <div className="lead-modal-files-upload">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedFileTypes}
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id={`file-upload-${lead?.id}`}
                      />
                      <label 
                        htmlFor={`file-upload-${lead?.id}`}
                        className={`files-upload-btn ${uploadingFile ? 'uploading' : ''}`}
                      >
                        {uploadingFile ? (
                          <>
                            <span className="upload-spinner"></span>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <i className="fa fa-cloud-upload-alt"></i>
                            <span>Upload Files</span>
                          </>
                        )}
                      </label>
                      <p className="files-upload-hint">Photos, PDFs, Docs (max 10MB)</p>
                    </div>
                    
                    {/* Files Grid */}
                    {currentLead.files?.length > 0 && (
                      <div className="lead-modal-files-grid">
                        {currentLead.files.map((file) => (
                          <div 
                            key={file.id} 
                            className={`file-item file-type-${file.type}`}
                            onClick={() => setPreviewFile(file)}
                          >
                            <div className="file-item-preview">
                              {file.type === 'image' ? (
                                <img src={file.url} alt={file.name} />
                              ) : file.type === 'pdf' ? (
                                <div className="file-icon pdf">
                                  <i className="fa fa-file-pdf"></i>
                                </div>
                              ) : (
                                <div className="file-icon doc">
                                  <i className="fa fa-file-word"></i>
                                </div>
                              )}
                            </div>
                            <div className="file-item-info">
                              <span className="file-item-name" title={file.name}>
                                {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
                              </span>
                              <span className="file-item-size">{formatFileSize(file.size)}</span>
                            </div>
                            <button 
                              className="file-item-delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFile(file);
                              }}
                              title="Delete file"
                            >
                              <i className="fa fa-trash-alt"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* File Preview Modal */}
                {previewFile && (
                  <div className="file-preview-overlay" onClick={() => setPreviewFile(null)}>
                    <div className="file-preview-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="file-preview-header">
                        <span className="file-preview-name">{previewFile.name}</span>
                        <div className="file-preview-actions">
                          <a 
                            href={previewFile.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="file-preview-download"
                            title="Download"
                          >
                            <i className="fa fa-download"></i>
                          </a>
                          <button 
                            className="file-preview-close"
                            onClick={() => setPreviewFile(null)}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      </div>
                      <div className="file-preview-content">
                        {previewFile.type === 'image' ? (
                          <img src={previewFile.url} alt={previewFile.name} />
                        ) : previewFile.type === 'pdf' ? (
                          <iframe 
                            src={previewFile.url} 
                            title={previewFile.name}
                            width="100%" 
                            height="100%"
                          />
                        ) : (
                          <div className="file-preview-doc">
                            <i className="fa fa-file-word"></i>
                            <p>Document Preview</p>
                            <a 
                              href={previewFile.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="file-preview-open-btn"
                            >
                              <i className="fa fa-external-link-alt"></i>
                              Open in new tab
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Referred To Section - zeigt Leads die dieser Lead empfohlen hat */}
                {referredLeads.length > 0 && (
                  <div className="lead-modal-referred-to-section">
                    <div className="lead-modal-referred-to-header">
                      <i className="fa fa-share-alt"></i>
                      <span>Referred To</span>
                      <span className="referred-to-count">{referredLeads.length}</span>
                    </div>
                    <div className="referred-to-list">
                      {referredLeads.map(refLead => (
                        <div 
                          key={refLead.id}
                          className="referred-to-card"
                          onClick={() => {
                            if (onSelectLead) {
                              onSelectLead(refLead);
                            }
                          }}
                        >
                          <div className="referred-to-avatar">
                            {refLead.business_name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="referred-to-info">
                            <span className="referred-to-name">{refLead.business_name || "Unknown"}</span>
                            {refLead.lead_email && (
                              <span className="referred-to-email">{refLead.lead_email}</span>
                            )}
                          </div>
                          <i className="fa fa-chevron-right referred-to-arrow"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: Fields & Referral */}
              <div className="lead-modal-column lead-modal-column-right">
                {/* Contact Fields */}
                {Object.entries(ALL_FIELDS).map(([key, field]) => {
                  // Skip notes, referral fields, and business_name as they're shown separately
                  if (key === "lead_notes" || key === "lead_referral_by" || key === "business_name") return null;

                  const value = currentLead[key];
                  const hasValue = value && value.toString().trim() !== "";
                  const isEditing = editingField === key;

                  if (!hasValue && !isEditing) return null;

                  // Decision Makers → immer als Karten (nie als Roh-JSON /
                  // einzeiliges Textfeld) darstellen.
                  if (key === "lead_decision_makers") {
                    const dms = parseDecisionMakers(value);
                    if (dms.length === 0) return null;
                    return (
                      <div key={key} className="lead-modal-field field-type-decision_makers">
                        <div className="lead-modal-field-label">
                          <i className={`fa ${field.icon} field-icon`}></i>
                          {field.label}
                          <span className="lead-dm-count">{dms.length}</span>
                        </div>
                        <LeadDecisionMakersView raw={value} />
                      </div>
                    );
                  }

                  // Description → in benannte Sektionen zerlegt, mit Max-Höhe +
                  // Show-more Toggle. Bearbeiten läuft weiter über das Textfeld.
                  if (key === "lead_description" && !isEditing) {
                    return (
                      <div key={key} className="lead-modal-field field-type-description">
                        <div className="lead-modal-field-label">
                          <i className={`fa ${field.icon} field-icon`}></i>
                          {field.label}
                        </div>
                        <LeadDescriptionView
                          text={value}
                          onEdit={() => { setEditingField(key); setEditValue(value || ""); }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={key} className={`lead-modal-field field-type-${key.replace('lead_', '')}`}>
                    <div className="lead-modal-field-label">
                      <i className={`fa ${field.icon} field-icon`}></i>
                      {field.label}
                      {/* WhatsApp Badge für Phone */}
                      {key === "lead_phone" && currentLead.lead_phone_whatsapp && (
                        <span className="whatsapp-badge">
                          <i className="fab fa-whatsapp"></i>
                          WhatsApp
                        </span>
                      )}
                    </div>
                      {isEditing ? (
                        <div className="lead-modal-field-edit">
                          {key === "lead_description" ? (
                            <textarea
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder={`Enter ${field.label.toLowerCase()}...`}
                              autoFocus
                              rows={3}
                            />
                          ) : key === "lead_phone" ? (
                            <div className="phone-edit-wrapper">
                              <input
                                type="tel"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                placeholder="Enter phone..."
                                autoFocus
                              />
                              <button 
                                type="button"
                                className={`whatsapp-toggle ${currentLead.lead_phone_whatsapp ? 'active' : ''}`}
                                onClick={() => onSaveField(lead.id, 'lead_phone_whatsapp', !currentLead.lead_phone_whatsapp)}
                                title={currentLead.lead_phone_whatsapp ? 'WhatsApp enabled' : 'Enable WhatsApp'}
                              >
                                <i className="fab fa-whatsapp"></i>
                              </button>
                            </div>
                          ) : (
                            <input
                              type={key === "lead_email" ? "email" : "text"}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder={`Enter ${field.label.toLowerCase()}...`}
                              autoFocus
                            />
                          )}
                          <div className="lead-modal-field-actions">
                            <button
                              className="field-save-btn"
                              onClick={() => handleSave(key, editValue)}
                              disabled={saving}
                            >
                              {saving ? "..." : "Save"}
                            </button>
                            <button
                              className="field-cancel-btn"
                              onClick={() => {
                                setEditingField(null);
                                setEditValue("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="lead-modal-field-value"
                          onClick={() => {
                            setEditingField(key);
                            setEditValue(value || "");
                          }}
                        >
                          {key === "lead_website" && value ? (
                            <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                              {value}
                            </a>
                          ) : key === "lead_email" && value ? (
                            <a href={`mailto:${value}`} onClick={(e) => e.stopPropagation()}>
                              {value}
                            </a>
                          ) : key === "lead_instagram" && value ? (
                            <a href={`https://instagram.com/${value.replace("@", "")}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                              @{value.replace("@", "")}
                            </a>
                          ) : key === "lead_phone" && value ? (
                            <div className="phone-value-wrapper">
                              <span>{value}</span>
                              {currentLead.lead_phone_whatsapp && (
                                <a 
                                  href={`https://wa.me/${value.replace(/[^0-9]/g, '')}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="whatsapp-link"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Open in WhatsApp"
                                >
                                  <i className="fab fa-whatsapp"></i>
                                </a>
                              )}
                            </div>
                          ) : (
                            <span>{value}</span>
                          )}
                          <span className="edit-hint">Click to edit</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Referral By Section */}
                <div className="lead-modal-referral-section">
                  <div className="lead-modal-referral-header">
                    <i className="fa fa-user-friends"></i>
                    <span>Referred By</span>
                  </div>
                  {editingField === "lead_referral_by" ? (
                    <div className="referral-edit-wrapper">
                      <div className="referral-search-wrapper">
                        <input
                          type="text"
                          placeholder="Search leads to link as referrer..."
                          value={referralSearch}
                          onChange={(e) => {
                            setReferralSearch(e.target.value);
                            setShowReferralDropdown(true);
                          }}
                          onFocus={() => setShowReferralDropdown(true)}
                          autoFocus
                        />
                        {showReferralDropdown && filteredReferralLeads.length > 0 && (
                          <div className="referral-dropdown">
                            {filteredReferralLeads.map(refLead => (
                              <button
                                key={refLead.id}
                                type="button"
                                className="referral-dropdown-item"
                                onClick={async () => {
                                  await onSaveField(lead.id, 'lead_referral_by', refLead.id);
                                  setReferralSearch("");
                                  setShowReferralDropdown(false);
                                  setEditingField(null);
                                }}
                              >
                                <div className="referral-dropdown-avatar">
                                  {refLead.business_name?.charAt(0)?.toUpperCase() || "?"}
                                </div>
                                <div className="referral-dropdown-info">
                                  <span className="referral-dropdown-name">{refLead.business_name}</span>
                                  {refLead.lead_email && (
                                    <span className="referral-dropdown-email">{refLead.lead_email}</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="lead-modal-field-actions">
                        <button
                          className="field-cancel-btn"
                          onClick={() => {
                            setEditingField(null);
                            setReferralSearch("");
                            setShowReferralDropdown(false);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : currentLead.lead_referral_by ? (
                    <div className="referral-display-compact">
                      <div 
                        className="referral-display-card-compact"
                        onClick={() => {
                          const referrer = getReferrerLead(currentLead.lead_referral_by);
                          if (referrer && onSelectLead) {
                            onSelectLead(referrer);
                          }
                        }}
                      >
                        <div className="referral-display-avatar-compact">
                          {getReferrerLead(currentLead.lead_referral_by)?.business_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <span className="referral-display-name-compact">
                          {getReferrerLead(currentLead.lead_referral_by)?.business_name || "Unknown Lead"}
                        </span>
                        <i className="fa fa-chevron-right referral-display-arrow-compact"></i>
                      </div>
                      <div className="referral-actions-compact">
                        <button 
                          className="referral-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingField("lead_referral_by");
                          }}
                          title="Change referrer"
                        >
                          <i className="fa fa-pen"></i>
                        </button>
                        <button 
                          className="referral-action-btn remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSaveField(lead.id, 'lead_referral_by', null);
                          }}
                          title="Remove referrer"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="referral-empty"
                      onClick={() => setEditingField("lead_referral_by")}
                    >
                      <i className="fa fa-plus"></i>
                      <span>Add a referrer...</span>
                    </div>
                  )}
                </div>

                {/* Add Field Button */}
                {emptyFields.length > 0 && (
                  <div className="lead-modal-add-field">
                    {showAddField ? (
                      <div className="add-field-dropdown">
                        <div className="add-field-header">
                          <span>Add Field</span>
                          <button onClick={() => setShowAddField(false)}>×</button>
                        </div>
                        <div className="add-field-list">
                          {emptyFields.map((key) => (
                            <button
                              key={key}
                              className="add-field-option"
                              onClick={() => handleAddField(key)}
                            >
                              <i className={`fa ${ALL_FIELDS[key].icon} field-icon`}></i>
                              {ALL_FIELDS[key].label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="add-field-btn"
                        onClick={() => setShowAddField(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Field
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Lead Button */}
          {isNew && (
            <div className="new-lead-actions">
              <button
                className="new-lead-cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="new-lead-create-btn"
                onClick={handleCreateLead}
                disabled={!newLeadData.business_name.trim() || creatingLead}
              >
                {creatingLead ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus"></i>
                    Create Lead
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== HAUPTKOMPONENTE ==========

const Leads = ({ onEmptyChange }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedEmails, setExpandedEmails] = useState({}); // { showModal: leadId }
  const [leadEmails, setLeadEmails] = useState({}); // { leadId: [emails] }
  const [loadingEmails, setLoadingEmails] = useState({}); // { leadId: true/false }
  const [openDropdown, setOpenDropdown] = useState(null); // leadId für offenes Dropdown
  const [removingLead, setRemovingLead] = useState(null); // leadId das gerade entfernt wird
  const [showNewLeadModal, setShowNewLeadModal] = useState(false); // Modal für neuen Lead
  const [retargetingFlows, setRetargetingFlows] = useState([]); // Flows aus Settings
  const [customTouchpoints, setCustomTouchpoints] = useState({}); // Custom Touchpoints
  const [sortMode, setSortMode] = useState("todos"); // "recent" oder "todos"
  const [showHeaderAnimation, setShowHeaderAnimation] = useState(false); // Header fade-in
  const [completingStep, setCompletingStep] = useState({}); // { leadId: 'yes' | 'no' | 'action' } - Animation beim Step-Wechsel
  const [flowSelectModal, setFlowSelectModal] = useState(null); // leadId für Flow-Auswahl Modal
  
  // ========== PAPIERKORB SYSTEM ==========
  const [showTrash, setShowTrash] = useState(false); // Papierkorb-Ansicht aktiv?
  const [trashedLeads, setTrashedLeads] = useState([]); // Leads im Papierkorb
  const [trashConfirmModal, setTrashConfirmModal] = useState(null); // { leadId, leadName } für Confirm-Popup
  
  // ========== TOUCHPOINT ACTIVITY TRACKING ==========
  const [touchpointActivity, setTouchpointActivity] = useState([]); // Activity-Log
  const [activityStats, setActivityStats] = useState({
    last24h: 0,
    last7d: 0,
    total: 0,
    todayByType: {}
  });
  const [scoreboardVisible, setScoreboardVisible] = useState(false); // Scoreboard sichtbar?
  const [scoreboardFading, setScoreboardFading] = useState(false); // Fade-out Animation
  const scoreboardTimeoutRef = useRef(null);
  const scoreboardFadeTimeoutRef = useRef(null);

  const auth = getAuth();
  // Team-System: Hole die aktive World-ID (eigene oder Team-Owner's)
  const { userId: activeWorldUserId, hasFeature, canWrite, isOwner } = useActiveWorld();
  const userId = activeWorldUserId; // Verwende die aktive World-ID
  const navigate = useNavigate();

  useEffect(() => {
    if (!onEmptyChange) return undefined;
    onEmptyChange(!loading && leads.length === 0 && !showTrash);
    return undefined;
  }, [onEmptyChange, loading, leads.length, showTrash]);
  
  // Prüfe ob aktueller User der Owner ist (kann permanent löschen)
  const currentUserIsOwner = isOwner || auth.currentUser?.uid === userId;
  
  // Scoreboard für 3 Sekunden anzeigen
  const showScoreboardBriefly = () => {
    // Vorherige Timeouts löschen
    if (scoreboardTimeoutRef.current) {
      clearTimeout(scoreboardTimeoutRef.current);
    }
    if (scoreboardFadeTimeoutRef.current) {
      clearTimeout(scoreboardFadeTimeoutRef.current);
    }
    
    setScoreboardFading(false);
    setScoreboardVisible(true);
    
    // Nach 3 Sekunden fade-out starten
    scoreboardTimeoutRef.current = setTimeout(() => {
      setScoreboardFading(true);
      
      // Nach der Fade-Animation (300ms) verstecken
      scoreboardFadeTimeoutRef.current = setTimeout(() => {
        setScoreboardVisible(false);
        setScoreboardFading(false);
      }, 300);
    }, 3000);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  // Retargeting Flows aus Firebase laden
  useEffect(() => {
    if (!userId) return;
    
    const flowsRef = ref(database, `World/${userId}/settings/leadFlows`);
    const unsubscribe = onValue(flowsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadedFlows = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setRetargetingFlows(loadedFlows.filter(f => f.isActive));
      } else {
        setRetargetingFlows([]);
      }
    });

    // Custom Touchpoints laden
    const customTpRef = ref(database, `World/${userId}/settings/customTouchpoints`);
    const unsubscribeTp = onValue(customTpRef, (snapshot) => {
      const data = snapshot.val();
      setCustomTouchpoints(data || {});
    });

    return () => {
      unsubscribe();
      unsubscribeTp();
    };
  }, [userId]);

  // Leads aus Firestore laden
  useEffect(() => {
    const fetchLeads = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        const dbRef = collection(firestore, `World/${userId}/db`);
        const leadsQuery = query(
          dbRef,
          where("lead_status", "==", "Lead"),
          orderBy("lead_since", "desc")
        );

        const snapshot = await getDocs(leadsQuery);
        const leadsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter: Nur Leads ohne trashedAt anzeigen (Papierkorb wird separat geladen)
        const activeLeads = leadsData.filter(lead => !lead.trashedAt);
        setLeads(activeLeads);
      } catch (error) {
        console.error("Error fetching leads:", error);
        // Fallback: Versuche ohne orderBy
        try {
          const dbRef = collection(firestore, `World/${userId}/db`);
          const leadsQuery = query(dbRef, where("lead_status", "==", "Lead"));
          const snapshot = await getDocs(leadsQuery);
          const leadsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          // Filter: Nur Leads ohne trashedAt anzeigen
          const activeLeads = leadsData.filter(lead => !lead.trashedAt);
          setLeads(activeLeads);
        } catch (fallbackError) {
          console.error("Fallback query also failed:", fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [userId]);

  // Header Animation starten wenn Daten geladen sind
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        setShowHeaderAnimation(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShowHeaderAnimation(false);
    }
  }, [loading]);

  // ========== TOUCHPOINT ACTIVITY LADEN ==========
  useEffect(() => {
    const fetchActivity = async () => {
      if (!userId) return;

      try {
        const activityRef = collection(firestore, `World/${userId}/touchpointActivity`);
        // Lade nur Aktivitäten der letzten 7 Tage für Performance
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const activityQuery = query(
          activityRef,
          where("timestamp", ">=", sevenDaysAgo),
          orderBy("timestamp", "desc")
        );

        const snapshot = await getDocs(activityQuery);
        const activityData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTouchpointActivity(activityData);

        // Berechne Stats
        const now = Date.now();
        const oneDayAgo = now - (24 * 60 * 60 * 1000);
        
        const last24h = activityData.filter(a => a.timestamp >= oneDayAgo);
        const todayByType = last24h.reduce((acc, a) => {
          acc[a.type] = (acc[a.type] || 0) + 1;
          return acc;
        }, {});

        setActivityStats({
          last24h: last24h.length,
          last7d: activityData.length,
          total: activityData.length, // Könnte später separat gezählt werden
          todayByType,
          totalPoints24h: last24h.reduce((sum, a) => sum + (a.points || 1), 0)
        });

      } catch (error) {
        console.error("Error fetching touchpoint activity:", error);
      }
    };

    fetchActivity();
  }, [userId]);

  // ========== TOUCHPOINT ACTIVITY LOGGEN ==========
  const logTouchpointActivity = async (type, leadId, leadName, source = "manual", points = 1) => {
    if (!userId) return;

    const activityEntry = {
      type,
      leadId,
      leadName: leadName || "Unknown",
      timestamp: Date.now(),
      source, // "flow" oder "manual"
      points
    };

    try {
      // In Firestore speichern
      const activityRef = collection(firestore, `World/${userId}/touchpointActivity`);
      const docRef = await addDoc(activityRef, activityEntry);

      // Lokalen State aktualisieren
      const newEntry = { id: docRef.id, ...activityEntry };
      setTouchpointActivity(prev => [newEntry, ...prev]);

      // Stats aktualisieren
      setActivityStats(prev => ({
        ...prev,
        last24h: prev.last24h + 1,
        last7d: prev.last7d + 1,
        total: prev.total + 1,
        totalPoints24h: (prev.totalPoints24h || 0) + points,
        todayByType: {
          ...prev.todayByType,
          [type]: (prev.todayByType[type] || 0) + 1
        }
      }));

      // Scoreboard kurz anzeigen
      showScoreboardBriefly();

      // Fire-and-forget: anonymized sales analytics
      try { logSalesAnalytics(userId, leadId, { touchpointType: type }); } catch (_) {}

      return true;
    } catch (error) {
      console.error("Error logging touchpoint activity:", error);
      return false;
    }
  };

  // Feld speichern (wird von Modal aufgerufen)
  const handleSaveField = async (leadId, fieldKey, value) => {
    if (!userId || !leadId) return false;

    // Finde den aktuellen Lead für Touchpoint-Tracking
    const currentLead = leads.find(l => l.id === leadId);

    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      await updateDoc(leadRef, {
        [fieldKey]: value,
        updated_at: Date.now(),
      });

      if (currentLead && fieldKey === 'touchpoints') {
        const previous = currentLead.touchpoints || {};
        const added = Object.keys(value || {})
          .filter((k) => previous[k] === undefined)
          .map((k) => (ALL_TOUCHPOINTS[k] || customTouchpoints[k] || {}).label || k);
        if (added.length > 0) {
          logContactActivity(firestore, userId, leadId, {
            type: 'edited',
            title: `Touchpoint: ${added.join(', ')}`,
            detail: 'in Leads',
          });
        }
      } else if (currentLead && JSON.stringify(currentLead[fieldKey] ?? '') !== JSON.stringify(value ?? '')) {
        const entry = describeEdit({ [fieldKey]: value }, currentLead);
        if (entry) logContactActivity(firestore, userId, leadId, entry);
      }

      // ========== TOUCHPOINT ACTIVITY TRACKING ==========
      // Wenn Touchpoints geändert wurden, logge die neuen/erhöhten Touchpoints
      if (fieldKey === 'touchpoints' && currentLead) {
        const oldTouchpoints = currentLead.touchpoints || {};
        const newTouchpoints = value || {};
        
        // Finde neue oder erhöhte Touchpoints
        Object.entries(newTouchpoints).forEach(([tpKey, tpValue]) => {
          const oldValue = oldTouchpoints[tpKey];
          const tpConfig = ALL_TOUCHPOINTS[tpKey] || customTouchpoints[tpKey];
          
          if (oldValue === undefined) {
            // Neuer Touchpoint hinzugefügt
            logTouchpointActivity(
              tpKey,
              leadId,
              currentLead.business_name,
              "manual",
              tpConfig?.points || 1
            );
          } else if (tpConfig?.hasCount && typeof tpValue === 'number' && tpValue > oldValue) {
            // Counter erhöht - logge die Differenz
            const diff = tpValue - oldValue;
            for (let i = 0; i < diff; i++) {
              logTouchpointActivity(
                tpKey,
                leadId,
                currentLead.business_name,
                "manual",
                tpConfig?.points || 1
              );
            }
          }
        });

        // Fire-and-forget: auto-detect meeting/deal outcomes from touchpoints
        try {
          const MEETING_TPS = ['zoom_call_scheduled', 'meet_in_person', 'sales_meeting', 'online_meeting', 'video_call', 'lunch_meeting', 'discovery_call'];
          const DEAL_TPS = ['contract_signed', 'payment_received'];
          const newKeys = Object.keys(newTouchpoints);
          if (newKeys.some(k => MEETING_TPS.includes(k) && !oldTouchpoints[k])) {
            logOutcomeAnalytics(userId, leadId, { meeting: true });
          }
          if (newKeys.some(k => DEAL_TPS.includes(k) && !oldTouchpoints[k])) {
            logOutcomeAnalytics(userId, leadId, {
              deal: true,
              dealValue: currentLead.lead_deal_value ? Number(currentLead.lead_deal_value) : undefined,
            });
          }
        } catch (_) {}
      }

      // Lokalen State aktualisieren
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, [fieldKey]: value } : lead
        )
      );

      // Auch selectedLead aktualisieren
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, [fieldKey]: value }));
      }

      return true;
    } catch (error) {
      console.error("Error saving field:", error);
      return false;
    }
  };


  // Neuen Lead erstellen
  const handleCreateLead = async (leadData) => {
    if (!userId) return false;

    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      
      const newLead = {
        ...leadData,
        // Das Formular sammelt Freitext, gespeichert wird die Note-Liste.
        lead_notes: normalizeLeadNotes(leadData.lead_notes).map(n =>
          n.createdAt ? n : createPrivateNote({ activity: "note", text: n.text })
        ),
        lead_status: "Lead",
        lead_since: Date.now(),
        created_at: Date.now(),
        updated_at: Date.now(),
        search_terms: buildSearchTerms(leadData),
      };

      const docRef = await addDoc(dbRef, newLead);
      
      // Füge den neuen Lead zur lokalen Liste hinzu (am Anfang)
      setLeads((prev) => [{
        id: docRef.id,
        ...newLead,
      }, ...prev]);

      return true;
    } catch (error) {
      console.error("Error creating lead:", error);
      return false;
    }
  };

  // ========== PAPIERKORB FUNKTIONEN ==========
  
  // Öffnet das Confirm-Popup für Trash
  const openTrashConfirm = (leadId, leadName) => {
    setTrashConfirmModal({ leadId, leadName });
    setOpenDropdown(null);
  };
  
  // Lead in Papierkorb verschieben + Status auf "Removed-Lead" setzen
  // Trackt wer (Owner/Member) und wann die Änderung gemacht wurde
  const confirmMoveToTrash = async () => {
    if (!trashConfirmModal || !userId) return;
    
    const { leadId } = trashConfirmModal;
    setRemovingLead(leadId);
    
    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      const now = Date.now();
      const currentUserId = auth.currentUser?.uid;
      const currentUserEmail = auth.currentUser?.email || 'Unknown';
      
      // Finde den Lead BEVOR wir ihn entfernen (für Papierkorb-Liste)
      const leadToTrash = leads.find(l => l.id === leadId);
      
      await updateDoc(leadRef, {
        // Papierkorb-Daten
        trashedAt: now,
        trashedBy: currentUserId,
        trashedByEmail: currentUserEmail, // Email des löschenden Users speichern
        
        // Neuer Status: "Removed-Lead" - so wissen wir, dass er mal ein Lead war
        lead_status: "Removed-Lead",
        lead_previous_status: "Lead", // Speichere den vorherigen Status für Wiederherstellung
        
        // Tracking: Wer hat die Änderung gemacht?
        removedAt: now,
        removedBy: currentUserId,
        removedByEmail: currentUserEmail,
        removedByRole: currentUserIsOwner ? "owner" : "member",
        
        updated_at: now,
      });
      
      // Aus der aktuellen Leads-Liste entfernen
      setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      
      // Direkt zur Papierkorb-Liste hinzufügen (ohne Refresh!)
      if (leadToTrash) {
        const trashedLead = {
          ...leadToTrash,
          trashedAt: now,
          trashedBy: currentUserId,
          trashedByEmail: currentUserEmail,
          lead_status: "Removed-Lead",
          lead_previous_status: "Lead",
          removedAt: now,
          removedBy: currentUserId,
          removedByEmail: currentUserEmail,
          removedByRole: currentUserIsOwner ? "owner" : "member",
          updated_at: now,
        };
        setTrashedLeads((prev) => [trashedLead, ...prev]);
      }
      
      // Schließe Modal falls offen
      if (selectedLead?.id === leadId) {
        setSelectedLead(null);
      }
      
      setTrashConfirmModal(null);
    } catch (error) {
      console.error("Error moving lead to trash:", error);
    } finally {
      setRemovingLead(null);
    }
  };
  
  // Lead aus Papierkorb wiederherstellen (alle Team-Mitglieder können das)
  // Setzt Status zurück auf "Lead" und entfernt Trash/Removed-Tracking
  // leadData wird direkt übergeben um Stale Closure zu vermeiden
  const restoreFromTrash = async (leadId, leadName, leadData = null) => {
    if (!userId || !leadId) return;
    
    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      const now = Date.now();
      const currentUserId = auth.currentUser?.uid;
      const currentUserEmail = auth.currentUser?.email || 'Unknown';
      
      await updateDoc(leadRef, {
        // Papierkorb-Daten entfernen
        trashedAt: null,
        trashedBy: null,
        trashedByEmail: null,
        
        // Status zurück auf "Lead" setzen
        lead_status: "Lead",
        
        // Wiederherstellungs-Tracking
        restoredAt: now,
        restoredBy: currentUserId,
        restoredByEmail: currentUserEmail,
        restoredByRole: currentUserIsOwner ? "owner" : "member",
        
        updated_at: now,
      });
      
      // Aus Papierkorb-Liste entfernen UND Lead-Daten holen
      let foundLead = leadData;
      setTrashedLeads((prev) => {
        if (!foundLead) {
          foundLead = prev.find((lead) => lead.id === leadId);
        }
        return prev.filter((lead) => lead.id !== leadId);
      });
      
      // Zur Haupt-Liste hinzufügen (sofort sichtbar ohne Refresh!)
      // Kurzes Timeout um sicherzustellen dass trashedLeads aktualisiert wurde
      setTimeout(() => {
        if (foundLead) {
          const restoredLead = { 
            ...foundLead, 
            trashedAt: null, 
            trashedBy: null,
            trashedByEmail: null,
            lead_status: "Lead",
            restoredAt: now,
            restoredBy: currentUserId,
            restoredByEmail: currentUserEmail,
            restoredByRole: currentUserIsOwner ? "owner" : "member",
            updated_at: now,
          };
          setLeads((prev) => [restoredLead, ...prev]);
        }
      }, 10);
    } catch (error) {
      console.error("Error restoring lead from trash:", error);
      alert("Failed to restore lead. Please try again.");
    }
  };
  
  // Lead permanent löschen (NUR Owner!)
  const deletePermanently = async (leadId, leadName) => {
    if (!userId || !leadId) return;
    
    // Sicherheitscheck: Nur Owner darf permanent löschen
    if (!currentUserIsOwner) {
      alert("Only the account owner can permanently delete leads.");
      return;
    }
    
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE "${leadName || 'this lead'}"?\n\nThis action cannot be undone!`
    );
    if (!confirmed) return;
    
    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      await deleteDoc(leadRef);
      
      // Aus der Papierkorb-Liste entfernen
      setTrashedLeads((prev) => prev.filter((lead) => lead.id !== leadId));
    } catch (error) {
      console.error("Error deleting lead permanently:", error);
      alert("Failed to delete lead. Please try again.");
    }
  };
  
  // Papierkorb-Leads laden (Status ist jetzt "Removed-Lead")
  const loadTrashedLeads = async () => {
    if (!userId) return;
    
    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      // Nur nach Removed-Lead Status filtern - das ist eindeutig für Papierkorb
      const trashQuery = query(
        dbRef, 
        where("lead_status", "==", "Removed-Lead")
      );
      
      const snapshot = await getDocs(trashQuery);
      const trashedData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sortiere nach trashedAt (neueste zuerst)
      trashedData.sort((a, b) => (b.trashedAt || 0) - (a.trashedAt || 0));
      
      setTrashedLeads(trashedData);
    } catch (error) {
      console.error("Error loading trashed leads:", error);
    }
  };
  
  // Papierkorb leeren (NUR Owner!)
  const emptyTrash = async () => {
    if (!currentUserIsOwner) {
      alert("Only the account owner can empty the trash.");
      return;
    }
    
    if (trashedLeads.length === 0) {
      alert("Trash is already empty.");
      return;
    }
    
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE ALL ${trashedLeads.length} leads in trash?\n\nThis action cannot be undone!`
    );
    if (!confirmed) return;
    
    try {
      const batch = writeBatch(firestore);
      trashedLeads.forEach((lead) => {
        const leadRef = doc(firestore, `World/${userId}/db/${lead.id}`);
        batch.delete(leadRef);
      });
      await batch.commit();
      
      setTrashedLeads([]);
      alert("Trash emptied successfully.");
    } catch (error) {
      console.error("Error emptying trash:", error);
      alert("Failed to empty trash. Please try again.");
    }
  };
  
  // Wenn Papierkorb-Ansicht aktiviert wird, Trash-Leads laden
  useEffect(() => {
    if (showTrash) {
      loadTrashedLeads();
    }
  }, [showTrash, userId]);

  // ========== FLOW MANAGEMENT ==========
  
  // Helper: Flow zur History hinzufügen
  const addFlowToHistory = (existingHistory, activeFlow, flow, status, now) => {
    if (!activeFlow) return existingHistory || [];
    
    const historyEntry = {
      flowId: activeFlow.flowId,
      flowName: activeFlow.flowName || flow?.name || 'Unknown Flow',
      startedAt: activeFlow.startedAt,
      endedAt: now,
      status: status, // 'completed', 'cancelled', 'replaced'
      stepsCompleted: activeFlow.currentStepIndex >= 0 ? activeFlow.currentStepIndex + 1 : 0,
      totalSteps: flow?.steps?.length || 0,
      stepHistory: activeFlow.stepHistory || {},
      pathTaken: activeFlow.pathTaken || [],
    };
    
    return [...(existingHistory || []), historyEntry];
  };
  
  // Flow für Lead starten
  const startFlowForLead = async (leadId, flowId) => {
    if (!userId || !leadId || !flowId) return false;
    
    const flow = retargetingFlows.find(f => f.id === flowId);
    if (!flow || !flow.steps || flow.steps.length === 0) return false;

    const lead = leads.find(l => l.id === leadId);

    try {
      const now = Date.now();
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      
      // Falls ein aktiver Flow existiert, diesen in die History speichern
      let updatedFlowHistory = lead?.flowHistory || [];
      if (lead?.activeFlow && lead.activeFlow.currentStepIndex >= 0) {
        const oldFlow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
        updatedFlowHistory = addFlowToHistory(updatedFlowHistory, lead.activeFlow, oldFlow, 'replaced', now);
      }
      
      // Berechne wann der erste Step fällig ist
      const firstStepDelay = parseInt(flow.steps[0].delayDays) || 0;
      const firstStepAvailableAt = now + (firstStepDelay * 24 * 60 * 60 * 1000);
      
      const activeFlow = {
        flowId: flowId,
        flowName: flow.name,
        startedAt: now,
        currentStepIndex: 0,
        stepHistory: {},
        nextStepAvailableAt: firstStepAvailableAt,
      };

      await updateDoc(leadRef, {
        activeFlow: activeFlow,
        flowHistory: updatedFlowHistory,
        updated_at: now,
      });

      // Enqueue first step for backend processing if auto or delay
      try {
        const firstStep = flow.steps[0];
        const autoTypes = ['auto_send_email', 'auto_check_reply'];
        const isDelay = firstStep?.nodeType === 'delay' && (parseInt(firstStep.delayDays) || 0) > 0;
        if (firstStep && (autoTypes.includes(firstStep.nodeType) || isDelay)) {
          const queueRef = ref(database, `automationQueue/${userId}/${leadId}`);
          const queueEntry = {
            userId, flowId: flowId, flowName: flow.name || '', flowType: 'retargeting',
            stepIndex: 0, stepType: isDelay ? 'resolve_delay' : firstStep.nodeType,
            executeAt: firstStepAvailableAt, retryCount: 0, createdAt: now,
          };
          if (firstStep.nodeType === 'auto_send_email') {
            queueEntry.templateId = firstStep.templateId || '';
            queueEntry.templateName = firstStep.templateName || '';
            queueEntry.senderId = firstStep.senderId || 'random';
          }
          if (firstStep.nodeType === 'auto_check_reply') {
            queueEntry.timeoutDays = firstStep.timeoutDays || 3;
            queueEntry.checkStartedAt = now;
          }
          rtdbSet(queueRef, queueEntry);
        }
      } catch (_) { /* never break flow start */ }

      // Lokalen State aktualisieren
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, activeFlow, flowHistory: updatedFlowHistory } : l
        )
      );

      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, activeFlow, flowHistory: updatedFlowHistory }));
      }

      return true;
    } catch (error) {
      console.error("Error starting flow:", error);
      return false;
    }
  };

  // Helper: Find the next node based on edges (for visual flows with branching)
  const findNextNodeFromEdges = (flow, currentNodeId, sourceHandle = null) => {
    if (!flow.visualData?.edges) return null;
    
    // Find edges that start from the current node
    const outgoingEdges = flow.visualData.edges.filter(edge => {
      if (edge.source !== currentNodeId) return false;
      // If sourceHandle is specified (for splits/conditions), match it
      if (sourceHandle && edge.sourceHandle !== sourceHandle) return false;
      return true;
    });
    
    if (outgoingEdges.length === 0) return null;
    
    // Get the target node ID
    const targetNodeId = outgoingEdges[0].target;
    
    // Find the step index for this node
    const stepIndex = flow.steps.findIndex(step => step.id === targetNodeId);
    
    return stepIndex >= 0 ? stepIndex : null;
  };

  // Flow-Step als erledigt markieren
  const completeFlowStep = async (leadId, stepIndex) => {
    if (!userId || !leadId) return false;

    const lead = leads.find(l => l.id === leadId);
    if (!lead?.activeFlow) return false;

    const flow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
    if (!flow || !flow.steps) return false;

    // Prüfe ob der Step wirklich fällig ist (Delay eingehalten)
    const now = Date.now();
    const stepAvailableAt = lead.activeFlow.nextStepAvailableAt || 0;
    if (now < stepAvailableAt) {
      console.warn("Step not yet available. Time remaining:", stepAvailableAt - now);
      return false; // Step noch nicht fällig!
    }

    // Hole den aktuellen Step für Touchpoint-Tracking
    const currentStep = flow.steps[stepIndex];
    if (!currentStep) return false;

    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      
      // Speichere Step-History mit Timestamps
      let splitChoice = null;
      const stepHistory = {
        ...(lead.activeFlow.stepHistory || {}),
        [stepIndex]: { completedAt: now }
      };
      
      // Determine next step based on node type and edges
      let nextStepIndex = null;
      let isFlowComplete = false;
      
      // Check if this is an END node - flow is complete!
      if (currentStep.nodeType === 'end' || currentStep.type === 'end') {
        isFlowComplete = true;
        nextStepIndex = -1;
      } else if (currentStep.nodeType === 'split') {
        // A/B Split: randomly choose path based on ratio
        const splitA = currentStep.splitA || 50;
        const random = Math.random() * 100;
        splitChoice = random < splitA ? 'a' : 'b';
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id, splitChoice);
        stepHistory[stepIndex].splitChoice = splitChoice;
      } else {
        // Regular node: follow the single outgoing edge
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id);
      }
      
      // Fallback to sequential if no edge found (only if not already complete)
      if (!isFlowComplete && nextStepIndex === null) {
        nextStepIndex = stepIndex + 1;
      }
      
      // Check if we've run out of steps
      if (!isFlowComplete) {
        isFlowComplete = nextStepIndex >= (flow.steps?.length || 0) || nextStepIndex < 0;
      }

      // Auto-complete non-interactive nodes (tags, auto-conditions like is_favorite)
      let autoCompleteCount = 0;
      const maxAutoComplete = 10;
      while (!isFlowComplete && flow.steps[nextStepIndex] && autoCompleteCount < maxAutoComplete) {
        const nextStep = flow.steps[nextStepIndex];

        // End node
        if (nextStep.nodeType === 'end' || nextStep.type === 'end') {
          isFlowComplete = true;
          stepHistory[nextStepIndex] = { completedAt: now, autoCompleted: true };
          break;
        }

        // Tag node or zero-delay node
        const isNonInteractive =
          nextStep.nodeType === 'tag' ||
          (nextStep.nodeType === 'delay' && (parseInt(nextStep.delayDays) || 0) === 0);

        // Auto-evaluable condition (e.g. is_favorite, has_phone, has_email, has_company)
        const autoDecision = nextStep.nodeType === 'condition' && nextStep.condition
          ? autoEvaluateCondition(nextStep.condition, lead)
          : null;
        const isAutoCondition = autoDecision !== null;

        if (!isNonInteractive && !isAutoCondition) break;

        stepHistory[nextStepIndex] = { completedAt: now, autoCompleted: true };

        let followingIndex;
        if (isAutoCondition) {
          stepHistory[nextStepIndex].decision = autoDecision;
          stepHistory[nextStepIndex].autoEvaluated = true;
          // Apply tags if it's a tag node (already handled above for non-auto)
          followingIndex = findNextNodeFromEdges(flow, nextStep.id, autoDecision);
        } else {
          // Tag node: apply tags
          if (nextStep.nodeType === 'tag' && nextStep.tags && Array.isArray(nextStep.tags)) {
            nextStep.tags.forEach(tag => {
              const tagKey = tag.id || tag.name.toLowerCase().replace(/\s+/g, '_');
              updatedTags[tagKey] = {
                id: tag.id, name: tag.name, color: tag.color || '#6b7280',
                icon: tag.icon || 'fa-tag', addedAt: now, addedBy: 'flow',
                flowId: lead.activeFlow.flowId, flowName: flow.name
              };
            });
          }
          followingIndex = findNextNodeFromEdges(flow, nextStep.id);
        }

        if (followingIndex === null || followingIndex < 0) {
          isFlowComplete = true;
          break;
        }
        nextStepIndex = followingIndex;
        autoCompleteCount++;
      }

      if (!isFlowComplete) {
        isFlowComplete = nextStepIndex >= (flow.steps?.length || 0) || nextStepIndex < 0;
      }

      // Berechne wann der nächste Step verfügbar ist (nutze delayDays!)
      let nextStepAvailableAt = null;
      if (!isFlowComplete && flow.steps[nextStepIndex]) {
        const nextStepDelay = parseInt(flow.steps[nextStepIndex].delayDays) || 0;
        nextStepAvailableAt = now + (nextStepDelay * 24 * 60 * 60 * 1000);
      }

      const updatedFlow = {
        ...lead.activeFlow,
        stepHistory,
        lastStepCompletedAt: now,
        nextStepAvailableAt, // Wann der nächste Step fällig ist
        currentStepIndex: isFlowComplete ? -1 : nextStepIndex,
        completedAt: isFlowComplete ? now : null,
        // Track the path taken
        pathTaken: [...(lead.activeFlow.pathTaken || []), { stepIndex, splitChoice, nextStepIndex }]
      };

      // ========== TOUCHPOINT TRACKING ==========
      // Füge den Step-Typ als Touchpoint hinzu oder erhöhe den Counter
      const touchpointType = currentStep.type;
      const touchpointConfig = ALL_TOUCHPOINTS[touchpointType] || customTouchpoints[touchpointType];
      
      let updatedTouchpoints = { ...(lead.touchpoints || {}) };
      let updatedTags = { ...(lead.tags || {}) };
      
      // Check if this is a tag node
      if (currentStep.nodeType === 'tag' && currentStep.tags && Array.isArray(currentStep.tags)) {
        // Add all tags from this step to the lead
        currentStep.tags.forEach(tag => {
          const tagKey = tag.id || tag.name.toLowerCase().replace(/\s+/g, '_');
          updatedTags[tagKey] = {
            id: tag.id,
            name: tag.name,
            color: tag.color || '#6b7280',
            icon: tag.icon || 'fa-tag',
            addedAt: now,
            addedBy: 'flow',
            flowId: lead.activeFlow.flowId,
            flowName: flow.name
          };
        });
      } else if (touchpointConfig) {
        if (touchpointConfig.hasCount) {
          // Touchpoint mit Counter - erhöhe um 1
          const currentCount = updatedTouchpoints[touchpointType] || 0;
          updatedTouchpoints[touchpointType] = currentCount + 1;
        } else {
          // Touchpoint ohne Counter - setze auf true
          updatedTouchpoints[touchpointType] = true;
        }
      } else {
        // Unbekannter Touchpoint (z.B. Custom) - als Counter behandeln
        const currentCount = updatedTouchpoints[touchpointType] || 0;
        updatedTouchpoints[touchpointType] = currentCount + 1;
      }

      // Falls Flow abgeschlossen, in History speichern
      let updatedFlowHistory = lead?.flowHistory || [];
      if (isFlowComplete) {
        updatedFlowHistory = addFlowToHistory(updatedFlowHistory, updatedFlow, flow, 'completed', now);
      }

      await updateDoc(leadRef, {
        activeFlow: isFlowComplete ? null : updatedFlow,
        flowHistory: updatedFlowHistory,
        touchpoints: updatedTouchpoints,
        tags: updatedTags,
        updated_at: now,
      });

      // Enqueue next step for backend processing
      try {
        if (isFlowComplete) {
          rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), null);
        } else {
          const ns = flow.steps[nextStepIndex];
          const autoTypes = ['auto_send_email', 'auto_check_reply'];
          const isDelay = ns?.nodeType === 'delay' && (parseInt(ns.delayDays) || 0) > 0;
          if (ns && (autoTypes.includes(ns.nodeType) || isDelay)) {
            const qEntry = {
              userId, flowId: lead.activeFlow.flowId, flowName: flow.name || '', flowType: 'retargeting',
              stepIndex: nextStepIndex, stepType: isDelay ? 'resolve_delay' : ns.nodeType,
              executeAt: nextStepAvailableAt || now, retryCount: 0, createdAt: now,
            };
            if (ns.nodeType === 'auto_send_email') { qEntry.templateId = ns.templateId || ''; qEntry.templateName = ns.templateName || ''; qEntry.senderId = ns.senderId || 'random'; }
            if (ns.nodeType === 'auto_check_reply') { qEntry.timeoutDays = ns.timeoutDays || 3; qEntry.checkStartedAt = now; }
            rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), qEntry);
          } else {
            rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), null);
          }
        }
      } catch (_) { /* never break flow */ }

      // ========== ACTIVITY TRACKING ==========
      // Logge diese Aktion für den Spielstand
      const touchpointPoints = touchpointConfig?.points || 1;
      logTouchpointActivity(
        touchpointType,
        leadId,
        lead.business_name,
        "flow", // Quelle: Flow-Automation
        touchpointPoints
      );

      // Lokalen State aktualisieren (inkl. Touchpoints & Tags!)
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { 
            ...l, 
            activeFlow: isFlowComplete ? null : updatedFlow, 
            flowHistory: updatedFlowHistory,
            touchpoints: updatedTouchpoints, 
            tags: updatedTags 
          } : l
        )
      );

      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ 
          ...prev, 
          activeFlow: isFlowComplete ? null : updatedFlow, 
          flowHistory: updatedFlowHistory,
          touchpoints: updatedTouchpoints, 
          tags: updatedTags 
        }));
      }

      return true;
    } catch (error) {
      console.error("Error completing flow step:", error);
      return false;
    }
  };

  // Complete a condition step with a decision (yes/no)
  const completeFlowStepWithDecision = async (leadId, stepIndex, decision) => {
    if (!userId || !leadId) return false;

    const lead = leads.find(l => l.id === leadId);
    if (!lead?.activeFlow) return false;

    const flow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
    if (!flow || !flow.steps) return false;

    const currentStep = flow.steps[stepIndex];
    if (!currentStep) return false;

    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      const now = Date.now();
      
      // Save decision in step history
      const stepHistory = {
        ...(lead.activeFlow.stepHistory || {}),
        [stepIndex]: { 
          completedAt: now,
          decision: decision,
          conditionType: currentStep.condition
        }
      };
      
      // Find the next step based on the decision using edges
      // For conditions: 'yes' = 'yes' handle, 'no' = 'no' handle
      const sourceHandle = decision; // 'yes' or 'no'
      let nextStepIndex = findNextNodeFromEdges(flow, currentStep.id, sourceHandle);
      
      // Fallback: if no edge found, try without handle or use sequential
      if (nextStepIndex === null) {
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id);
      }
      if (nextStepIndex === null) {
        // Last fallback: sequential (old behavior)
        nextStepIndex = stepIndex + 1;
      }
      
      let isFlowComplete = nextStepIndex >= (flow.steps?.length || 0) || nextStepIndex < 0;

      // Auto-complete non-interactive follow-up nodes (tags, auto-conditions like is_favorite)
      let autoCompleteCountDecision = 0;
      const maxAutoCompleteDecision = 10;
      while (!isFlowComplete && flow.steps[nextStepIndex] && autoCompleteCountDecision < maxAutoCompleteDecision) {
        const nextStep = flow.steps[nextStepIndex];
        if (nextStep.nodeType === 'end' || nextStep.type === 'end') {
          isFlowComplete = true;
          stepHistory[nextStepIndex] = { completedAt: now, autoCompleted: true };
          break;
        }
        const isNonInteractive =
          nextStep.nodeType === 'tag' ||
          (nextStep.nodeType === 'delay' && (parseInt(nextStep.delayDays) || 0) === 0);
        const autoDecisionVal = nextStep.nodeType === 'condition' && nextStep.condition
          ? autoEvaluateCondition(nextStep.condition, lead) : null;
        const isAutoCondition = autoDecisionVal !== null;
        if (!isNonInteractive && !isAutoCondition) break;
        stepHistory[nextStepIndex] = { completedAt: now, autoCompleted: true };
        let followIdx;
        if (isAutoCondition) {
          stepHistory[nextStepIndex].decision = autoDecisionVal;
          stepHistory[nextStepIndex].autoEvaluated = true;
          followIdx = findNextNodeFromEdges(flow, nextStep.id, autoDecisionVal);
        } else {
          followIdx = findNextNodeFromEdges(flow, nextStep.id);
        }
        if (followIdx === null || followIdx < 0) { isFlowComplete = true; break; }
        nextStepIndex = followIdx;
        autoCompleteCountDecision++;
      }
      if (!isFlowComplete) {
        isFlowComplete = nextStepIndex >= (flow.steps?.length || 0) || nextStepIndex < 0;
      }

      let nextStepAvailableAt = null;
      if (!isFlowComplete && flow.steps[nextStepIndex]) {
        const nextStepDelay = parseInt(flow.steps[nextStepIndex].delayDays) || 0;
        nextStepAvailableAt = now + (nextStepDelay * 24 * 60 * 60 * 1000);
      }

      const updatedFlow = {
        ...lead.activeFlow,
        stepHistory,
        lastStepCompletedAt: now,
        lastDecision: decision,
        nextStepAvailableAt,
        currentStepIndex: isFlowComplete ? -1 : nextStepIndex,
        completedAt: isFlowComplete ? now : null,
        // Track the path taken
        pathTaken: [...(lead.activeFlow.pathTaken || []), { stepIndex, decision, nextStepIndex }]
      };

      // Add a tag based on decision if it's a qualifying question
      let updatedTags = { ...(lead.tags || {}) };
      
      // Auto-tag based on certain decisions
      if (currentStep.condition === 'qualified' && decision === 'yes') {
        updatedTags['qualified'] = { id: 'qualified', name: 'Qualified', color: '#22c55e', icon: 'fa-check-circle', addedAt: now, addedBy: 'flow_decision' };
      } else if (currentStep.condition === 'qualified' && decision === 'no') {
        updatedTags['not_qualified'] = { id: 'not_qualified', name: 'Not Qualified', color: '#6b7280', icon: 'fa-times-circle', addedAt: now, addedBy: 'flow_decision' };
      } else if (currentStep.condition === 'shown_interest' && decision === 'yes') {
        updatedTags['interested'] = { id: 'interested', name: 'Interested', color: '#10b981', icon: 'fa-thumbs-up', addedAt: now, addedBy: 'flow_decision' };
      } else if (currentStep.condition === 'decision_maker' && decision === 'yes') {
        updatedTags['decision_maker'] = { id: 'decision_maker', name: 'Decision Maker', color: '#ec4899', icon: 'fa-user-tie', addedAt: now, addedBy: 'flow_decision' };
      } else if (currentStep.condition === 'has_budget' && decision === 'yes') {
        updatedTags['high_budget'] = { id: 'high_budget', name: 'High Budget', color: '#22c55e', icon: 'fa-money-bill-wave', addedAt: now, addedBy: 'flow_decision' };
      } else if (currentStep.condition === 'good_fit' && decision === 'yes') {
        updatedTags['dream_client'] = { id: 'dream_client', name: 'Dream Client', color: '#8b5cf6', icon: 'fa-star', addedAt: now, addedBy: 'flow_decision' };
      }

      // Falls Flow abgeschlossen, in History speichern
      let updatedFlowHistory = lead?.flowHistory || [];
      if (isFlowComplete) {
        updatedFlowHistory = addFlowToHistory(updatedFlowHistory, updatedFlow, flow, 'completed', now);
      }

      await updateDoc(leadRef, {
        activeFlow: isFlowComplete ? null : updatedFlow,
        flowHistory: updatedFlowHistory,
        tags: updatedTags,
        updated_at: now,
      });

      // Enqueue next step for backend processing
      try {
        if (isFlowComplete) {
          rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), null);
        } else {
          const ns = flow.steps[nextStepIndex];
          const autoTypes = ['auto_send_email', 'auto_check_reply'];
          const isDelay = ns?.nodeType === 'delay' && (parseInt(ns.delayDays) || 0) > 0;
          if (ns && (autoTypes.includes(ns.nodeType) || isDelay)) {
            const qEntry = {
              userId, flowId: lead.activeFlow.flowId, flowName: flow.name || '', flowType: 'retargeting',
              stepIndex: nextStepIndex, stepType: isDelay ? 'resolve_delay' : ns.nodeType,
              executeAt: nextStepAvailableAt || now, retryCount: 0, createdAt: now,
            };
            if (ns.nodeType === 'auto_send_email') { qEntry.templateId = ns.templateId || ''; qEntry.templateName = ns.templateName || ''; qEntry.senderId = ns.senderId || 'random'; }
            if (ns.nodeType === 'auto_check_reply') { qEntry.timeoutDays = ns.timeoutDays || 3; qEntry.checkStartedAt = now; }
            rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), qEntry);
          } else {
            rtdbSet(ref(database, `automationQueue/${userId}/${leadId}`), null);
          }
        }
      } catch (_) { /* never break flow */ }

      // Update local state
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { 
            ...l, 
            activeFlow: isFlowComplete ? null : updatedFlow, 
            flowHistory: updatedFlowHistory,
            tags: updatedTags 
          } : l
        )
      );

      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ 
          ...prev, 
          activeFlow: isFlowComplete ? null : updatedFlow, 
          flowHistory: updatedFlowHistory,
          tags: updatedTags 
        }));
      }

      return true;
    } catch (error) {
      console.error("Error completing flow step with decision:", error);
      return false;
    }
  };

  // Flow stoppen
  const stopFlowForLead = async (leadId) => {
    if (!userId || !leadId) return false;

    const lead = leads.find(l => l.id === leadId);

    try {
      const now = Date.now();
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      
      // Flow in die History speichern (status: cancelled)
      let updatedFlowHistory = lead?.flowHistory || [];
      if (lead?.activeFlow && lead.activeFlow.currentStepIndex >= 0) {
        const flow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
        updatedFlowHistory = addFlowToHistory(updatedFlowHistory, lead.activeFlow, flow, 'cancelled', now);
      }

      await updateDoc(leadRef, {
        activeFlow: null,
        flowHistory: updatedFlowHistory,
        updated_at: now,
      });

      // Lokalen State aktualisieren
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, activeFlow: null, flowHistory: updatedFlowHistory } : l
        )
      );

      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, activeFlow: null, flowHistory: updatedFlowHistory }));
      }

      return true;
    } catch (error) {
      console.error("Error stopping flow:", error);
      return false;
    }
  };

  // Flow-Historie zurücksetzen (alle abgeschlossenen Flows löschen)
  const resetFlowHistory = async (leadId) => {
    if (!userId || !leadId) return false;

    try {
      const leadRef = doc(firestore, `World/${userId}/db/${leadId}`);
      
      await updateDoc(leadRef, {
        flowHistory: [],
        updated_at: Date.now(),
      });

      // Lokalen State aktualisieren
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, flowHistory: [] } : l
        )
      );

      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, flowHistory: [] }));
      }

      return true;
    } catch (error) {
      console.error("Error resetting flow history:", error);
      return false;
    }
  };

  // Hilfsfunktion: Berechnet die "Dringlichkeit" eines Leads basierend auf fälligen Actions
  const getLeadUrgency = (lead) => {
    if (!lead.activeFlow || lead.activeFlow.currentStepIndex < 0) {
      return Infinity; // Kein aktiver Flow = ganz unten
    }
    const now = Date.now();
    const stepAvailableAt = lead.activeFlow.nextStepAvailableAt || 0;
    // Je länger überfällig, desto negativer (= weiter oben)
    // Je weiter in der Zukunft, desto positiver (= weiter unten)
    return stepAvailableAt - now;
  };

  // Gefilterte und sortierte Leads
  const filteredLeads = leads
    .filter((lead) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        lead.business_name?.toLowerCase().includes(searchLower) ||
        lead.lead_email?.toLowerCase().includes(searchLower) ||
        lead.lead_description?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (sortMode === "todos") {
        // Sortierung nach To-Dos: Am längsten überfällige zuerst
        const urgencyA = getLeadUrgency(a);
        const urgencyB = getLeadUrgency(b);
        
        // Wenn beide keine aktiven Flows haben, nach lead_since sortieren
        if (urgencyA === Infinity && urgencyB === Infinity) {
          return (b.lead_since || 0) - (a.lead_since || 0);
        }
        
        // Leads mit aktivem Flow vor Leads ohne
        if (urgencyA === Infinity) return 1;
        if (urgencyB === Infinity) return -1;
        
        // Negativer Wert = überfälliger = weiter oben
        return urgencyA - urgencyB;
      } else {
        // Standard: Nach Hinzugefügt-Datum (neueste zuerst)
        const dateA = a.lead_since || 0;
        const dateB = b.lead_since || 0;
        return dateB - dateA;
      }
    });

  // E-Mails für einen Lead laden (gesendet UND empfangen)
  const loadEmailsForLead = async (leadId, leadEmail) => {
    if (!userId || !leadEmail || leadEmails[leadId]) return;

    setLoadingEmails(prev => ({ ...prev, [leadId]: true }));

    // lead_email kann kommagetrennt sein → alle Adressen einzeln extrahieren
    const emailAddresses = leadEmail.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    
    try {
      let allEmails = [];
      const seenIds = new Set();
      
      // 1. Gesendete E-Mails laden (world_sent) — duale Strategie
      try {
        const sentRef = collection(firestore, `world_sent/${userId}/emails`);

        // 1a. Query by leadId (fängt alle Outreach-Emails zuverlässig)
        const leadIdQuery = query(sentRef, where("leadId", "==", leadId));
        const leadIdSnapshot = await getDocs(leadIdQuery);
        
        leadIdSnapshot.docs.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            allEmails.push({
              id: doc.id,
              ...doc.data(),
              emailDirection: 'sent',
              timestamp: doc.data().sentAt || doc.data().createdAt || Date.now()
            });
          }
        });

        // 1b. Query by Email-Adresse(n) (fängt Replies und Edge-Cases)
        for (const addr of emailAddresses) {
          const toQuery = query(sentRef, where("to", "==", addr));
          const toSnapshot = await getDocs(toQuery);
          
          toSnapshot.docs.forEach(doc => {
            if (!seenIds.has(doc.id)) {
              seenIds.add(doc.id);
              allEmails.push({
                id: doc.id,
                ...doc.data(),
                emailDirection: 'sent',
                timestamp: doc.data().sentAt || doc.data().createdAt || Date.now()
              });
            }
          });
        }
      } catch (e) {
        console.log("Error loading sent emails:", e);
      }

      // 2. Empfangene E-Mails laden (world_inbox) — prüfe alle Email-Adressen
      try {
        const inboxRef = collection(firestore, `world_inbox/${userId}/emails`);
        const inboxSnapshot = await getDocs(inboxRef);
        
        const inboxEmails = inboxSnapshot.docs
          .filter(doc => {
            const from = (doc.data().from || "").toLowerCase();
            return emailAddresses.some(addr => from.includes(addr));
          })
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            emailDirection: 'received',
            timestamp: doc.data().date ? new Date(doc.data().date).getTime() : Date.now()
          }));
        allEmails = [...allEmails, ...inboxEmails];
      } catch (e) {
        console.log("Error loading inbox emails:", e);
      }

      // Nach Datum sortieren (neueste zuerst)
      allEmails.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      setLeadEmails(prev => ({ ...prev, [leadId]: allEmails }));
    } catch (error) {
      console.error("Error loading emails for lead:", error);
      setLeadEmails(prev => ({ ...prev, [leadId]: [] }));
    } finally {
      setLoadingEmails(prev => ({ ...prev, [leadId]: false }));
    }
  };

  // E-Mail Datum formatieren
  const formatEmailDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Card Komponente
  const LeadCard = ({ lead }) => {
    const hasWebsite = lead.lead_website && lead.lead_website.trim();
    const hasInstagram = lead.lead_instagram && lead.lead_instagram.trim();
    const hasEmail = lead.lead_email && lead.lead_email.trim();
    const hasPhone = lead.lead_phone && lead.lead_phone.trim();
    const hasWhatsApp = hasPhone && lead.lead_phone_whatsapp;
    const isDropdownOpen = openDropdown === lead.id;

    // Auto-complete Tag Steps - Tags werden automatisch im Hintergrund gesetzt (mit Verzögerung)
    React.useEffect(() => {
      if (!lead.activeFlow || lead.activeFlow.currentStepIndex < 0) return;
      const flow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
      if (!flow || !flow.steps) return;
      
      const currentStep = flow.steps[lead.activeFlow.currentStepIndex];
      if (!currentStep) return;
      
      // Wenn es ein Tag-Step ist und ready, automatisch abschließen (mit 1.5s Verzögerung)
      if (currentStep.nodeType === 'tag') {
        const now = Date.now();
        const stepDueTime = lead.activeFlow.nextStepAvailableAt || lead.activeFlow.startedAt;
        const isReady = now >= stepDueTime;
        
        if (isReady) {
          // Tag-Step mit Verzögerung automatisch abschließen
          const timeout = setTimeout(() => {
            completeFlowStep(lead.id, lead.activeFlow.currentStepIndex);
          }, 1500);
          
          return () => clearTimeout(timeout);
        }
      }
    }, [lead.activeFlow?.currentStepIndex, lead.activeFlow?.flowId, lead.id]);

    // Last email timestamp - das ist was in der Tabelle als "Days" angezeigt wird
    const lastEmailTimestamp = lead.lead_last_email?.timestamp;
    
    // Member Status Check - 3+ Gold, 6+ Platinum
    const referralCount = leads.filter(l => l.lead_referral_by === lead.id).length;
    const isPlatinumMember = referralCount >= 6;
    const isGoldMember = referralCount >= 3 && referralCount < 6;
    
    // Flow Status für Remaining Banner
    const getFlowStatus = () => {
      if (!lead.activeFlow || lead.activeFlow.currentStepIndex < 0) return null;
      const flow = retargetingFlows.find(f => f.id === lead.activeFlow.flowId);
      if (!flow || !flow.steps) return null;
      
      const currentStep = flow.steps[lead.activeFlow.currentStepIndex];
      if (!currentStep) return null;
      
      // Tag-Steps werden automatisch im Hintergrund abgeschlossen - keinen Banner anzeigen
      if (currentStep.nodeType === 'tag') return null;
      
      const now = Date.now();
      const stepDueTime = lead.activeFlow.nextStepAvailableAt || lead.activeFlow.startedAt;
      const isReady = now >= stepDueTime;
      const timeRemaining = stepDueTime - now;
      
      // Get touchpoint label - special handling for delay and end nodes
      let touchpoint;
      if (currentStep.nodeType === 'delay' || currentStep.type === 'delay') {
        touchpoint = { label: `Next Task ready in`, icon: "fa-hourglass-half" };
      } else if (currentStep.nodeType === 'end' || currentStep.type === 'end') {
        touchpoint = { label: "End This Flow", readyLabel: "End This Flow", icon: "fa-flag-checkered" };
      } else {
        touchpoint = ALL_TOUCHPOINTS[currentStep.type] || customTouchpoints[currentStep.type] || { label: currentStep.type, icon: "fa-circle" };
      }
      
      // Check if this is a manual condition
      const isCondition = currentStep.nodeType === 'condition';
      const manualConditions = ['shown_interest', 'qualified', 'decision_maker', 'has_budget', 'good_fit', 'ready_to_buy', 'needs_follow_up', 'meeting_went_well', 'responded_positively', 'custom_question', 'meeting_booked', 'deal_closed'];
      const isManualCondition = isCondition && manualConditions.includes(currentStep.condition);
      
      // Get condition label
      const conditionLabels = {
        shown_interest: 'Has Shown Interest?',
        qualified: 'Is Qualified?',
        decision_maker: 'Is Decision Maker?',
        has_budget: 'Has Budget?',
        good_fit: 'Good Fit?',
        ready_to_buy: 'Ready to Buy?',
        needs_follow_up: 'Needs Follow Up?',
        meeting_went_well: 'Meeting Went Well?',
        responded_positively: 'Responded Positively?',
        custom_question: currentStep.customQuestion || 'Custom Question',
        meeting_booked: 'Meeting Booked?',
        deal_closed: 'Deal Closed?',
      };
      const conditionLabel = isManualCondition ? conditionLabels[currentStep.condition] || currentStep.condition : null;
      
      return { flow, currentStep, isReady, timeRemaining, touchpoint, stepIndex: lead.activeFlow.currentStepIndex, isManualCondition, conditionLabel };
    };
    
    const flowStatus = getFlowStatus();
    const hasWaitingFlow = flowStatus && !flowStatus.isReady;
    const hasReadyFlow = flowStatus && flowStatus.isReady;

    // Zeit formatieren
    const formatTimeRemaining = (ms) => {
      if (ms <= 0) return "Ready!";
      const days = Math.floor(ms / (24 * 60 * 60 * 1000));
      const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m`;
      return "<1m";
    };
    
    // Check if lead has any active flow
    const hasAnyFlow = lead.activeFlow && lead.activeFlow.currentStepIndex >= 0;
    
    // Get the most recently completed flow from history
    const getLastCompletedFlow = () => {
      if (!lead.flowHistory || lead.flowHistory.length === 0) return null;
      // Find the most recent completed flow (sorted by endedAt, newest first)
      const completedFlows = lead.flowHistory
        .filter(f => f.status === 'completed')
        .sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
      return completedFlows.length > 0 ? completedFlows[0] : null;
    };
    
    const lastCompletedFlow = getLastCompletedFlow();
    
    return (
      <div className={`lead-card-wrapper has-banner-space ${hasWaitingFlow ? 'has-remaining' : ''} ${hasReadyFlow ? 'has-ready-banner' : ''}`}>
        {/* Add Flow Banner - appears when no active flow */}
        {!hasAnyFlow && (
          <div className="lead-remaining-banner add-flow" onClick={(e) => {
            e.stopPropagation();
            // Immer Flow-Modal öffnen - zeigt Empty-State wenn keine Flows existieren
            setFlowSelectModal(lead.id);
          }}>
            <div className="remaining-content">
              <div className="remaining-icon-area add-flow">
                <i className="fa fa-route"></i>
              </div>
              <div className="remaining-info">
                <span className="remaining-add-flow-text">Add retargeting flow</span>
                {lastCompletedFlow && (
                  <span className="remaining-completed-flow">
                    <i className="fa fa-check-circle"></i>
                    Completed: {lastCompletedFlow.flowName}
                  </span>
                )}
              </div>
              <div className="remaining-add-flow-btn">
                <i className="fa fa-plus"></i>
              </div>
            </div>
          </div>
        )}
        
        {/* Remaining Banner - appears behind card (waiting) */}
        {hasWaitingFlow && (
          <div className="lead-remaining-banner waiting">
            <div className="remaining-content">
              <div className="remaining-icon-area waiting">
                <i className="fa fa-clock"></i>
              </div>
              <div className="remaining-info">
                <div className="remaining-flow-progress">
                  {flowStatus.flow.name} • Step {flowStatus.stepIndex + 1}/{flowStatus.flow.steps.length}
                </div>
                <div className="remaining-title-row">
                  <span className="remaining-step">
                    <i className={`${flowStatus.touchpoint.isBrand ? 'fab' : 'fa'} ${flowStatus.touchpoint.icon}`}></i>
                    {flowStatus.touchpoint.readyLabel || flowStatus.touchpoint.label}
                  </span>
                  <span className="remaining-time">{formatTimeRemaining(flowStatus.timeRemaining)}</span>
                </div>
                {flowStatus.currentStep.note && (
                  <span className="remaining-note">"{flowStatus.currentStep.note}"</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Ready Banner - appears behind card (action needed!) */}
        {hasReadyFlow && (
          <div 
            className={`lead-remaining-banner ready ${flowStatus.isManualCondition ? 'is-decision' : ''} ${completingStep[lead.id] ? `completing completing-${completingStep[lead.id]}` : ''}`} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="remaining-content">
              <div className={`remaining-icon-area ready ${flowStatus.isManualCondition ? 'decision' : ''}`}>
                <i className={`fa ${flowStatus.isManualCondition ? 'fa-question' : 'fa-bell'}`}></i>
              </div>
              <div className="remaining-info">
                <div className="remaining-flow-progress ready">
                  {flowStatus.flow.name} • Step {flowStatus.stepIndex + 1}/{flowStatus.flow.steps.length}
                </div>
                <div className="remaining-title-row">
                  <span className="remaining-step ready">
                    <i className={`fa ${flowStatus.isManualCondition ? 'fa-user-check' : (flowStatus.touchpoint.isBrand ? 'fab' : 'fa')} ${flowStatus.isManualCondition ? '' : flowStatus.touchpoint.icon}`}></i>
                    {flowStatus.isManualCondition ? flowStatus.conditionLabel : (flowStatus.touchpoint.readyLabel || flowStatus.touchpoint.label)}
                  </span>
                  {!flowStatus.isManualCondition && <span className="remaining-ready-label">Ready!</span>}
                </div>
                {flowStatus.currentStep.note && (
                  <span className="remaining-note">"{flowStatus.currentStep.note}"</span>
                )}
              </div>
              
              {/* Decision buttons for manual conditions */}
              {flowStatus.isManualCondition ? (
                <div className="remaining-decision-btns">
                  <button 
                    className={`remaining-decision-btn yes ${completingStep[lead.id] === 'yes' ? 'selected' : ''}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (completingStep[lead.id]) return;
                      setCompletingStep(prev => ({ ...prev, [lead.id]: 'yes' }));
                      await completeFlowStepWithDecision(lead.id, flowStatus.stepIndex, 'yes');
                      // Kurz warten damit React re-rendern kann, dann State resetten
                      await new Promise(r => setTimeout(r, 50));
                      setCompletingStep(prev => ({ ...prev, [lead.id]: null }));
                    }}
                    title="Yes"
                    disabled={!!completingStep[lead.id]}
                  >
                    {completingStep[lead.id] === 'yes' ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa fa-check"></i>
                    )}
                  </button>
                  <button 
                    className={`remaining-decision-btn no ${completingStep[lead.id] === 'no' ? 'selected' : ''}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (completingStep[lead.id]) return;
                      setCompletingStep(prev => ({ ...prev, [lead.id]: 'no' }));
                      await completeFlowStepWithDecision(lead.id, flowStatus.stepIndex, 'no');
                      await new Promise(r => setTimeout(r, 50));
                      setCompletingStep(prev => ({ ...prev, [lead.id]: null }));
                    }}
                    title="No"
                    disabled={!!completingStep[lead.id]}
                  >
                    {completingStep[lead.id] === 'no' ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa fa-times"></i>
                    )}
                  </button>
                </div>
              ) : (
                <button 
                  className={`remaining-action-btn ${completingStep[lead.id] === 'action' ? 'selected' : ''}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (completingStep[lead.id]) return;
                    setCompletingStep(prev => ({ ...prev, [lead.id]: 'action' }));
                    await completeFlowStep(lead.id, flowStatus.stepIndex);
                    await new Promise(r => setTimeout(r, 50));
                    setCompletingStep(prev => ({ ...prev, [lead.id]: null }));
                  }}
                  disabled={!!completingStep[lead.id]}
                >
                  {completingStep[lead.id] === 'action' ? (
                    <i className="fa fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fa fa-check"></i>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
        
        <div className={`lead-card ${isPlatinumMember ? 'platinum-member' : isGoldMember ? 'gold-member' : ''}`} onClick={() => setSelectedLead(lead)}>
        {/* 3-Dots Dropdown Menu */}
        <div className="lead-card-menu">
          <button 
            className="lead-card-menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              setOpenDropdown(isDropdownOpen ? null : lead.id);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div className="lead-card-dropdown">
              {/* Start Flow Options */}
              {!lead.activeFlow && retargetingFlows.length > 0 && (
                <>
                  <div className="lead-card-dropdown-label">
                    <i className="fa fa-route"></i>
                    <span>Start Flow</span>
                  </div>
                  {retargetingFlows.map(flow => (
                    <button
                      key={flow.id}
                      className="lead-card-dropdown-item lead-dropdown-flow"
                      onClick={(e) => {
                        e.stopPropagation();
                        startFlowForLead(lead.id, flow.id);
                        setOpenDropdown(null);
                      }}
                    >
                      <span className="lead-dropdown-icon">
                        <i className="fa fa-play"></i>
                      </span>
                      <span>{flow.name}</span>
                    </button>
                  ))}
                  <div className="lead-card-dropdown-divider"></div>
                </>
              )}
              {/* Stop Flow Option */}
              {lead.activeFlow && (
                <>
                  <button
                    className="lead-card-dropdown-item lead-dropdown-stop"
                    onClick={(e) => {
                      e.stopPropagation();
                      stopFlowForLead(lead.id);
                      setOpenDropdown(null);
                    }}
                  >
                    <span className="lead-dropdown-icon">
                      <i className="fa fa-stop"></i>
                    </span>
                    <span>Stop Flow</span>
                  </button>
                  <div className="lead-card-dropdown-divider"></div>
                </>
              )}
              {/* Reset Flow History Option */}
              {lead.flowHistory && lead.flowHistory.length > 0 && (
                <>
                  <button
                    className="lead-card-dropdown-item lead-dropdown-reset"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Reset all completed flows for ${lead.business_name}? This will remove the flow history.`)) {
                        resetFlowHistory(lead.id);
                      }
                      setOpenDropdown(null);
                    }}
                  >
                    <span className="lead-dropdown-icon">
                      <i className="fa fa-history"></i>
                    </span>
                    <span>Reset Completed Flows</span>
                  </button>
                  <div className="lead-card-dropdown-divider"></div>
                </>
              )}
              {/* Move to Trash - setzt Lead auf "Removed-Lead" Status */}
              <button 
                className="lead-card-dropdown-item lead-dropdown-trash"
                onClick={(e) => {
                  e.stopPropagation();
                  openTrashConfirm(lead.id, lead.business_name);
                }}
                disabled={removingLead === lead.id}
              >
                <span className="lead-dropdown-icon">
                  <i className={`fa ${removingLead === lead.id ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}></i>
                </span>
                <span>{removingLead === lead.id ? 'Moving...' : 'Move to Trash'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="lead-card-header">
          <div className={`lead-card-avatar ${isPlatinumMember ? 'platinum-avatar' : isGoldMember ? 'gold-avatar' : ''} ${lead.profile_image?.url ? 'has-image' : ''}`}>
            {lead.profile_image?.url ? (
              <img src={lead.profile_image.url} alt={lead.business_name || 'Profile'} />
            ) : (
              lead.business_name?.charAt(0)?.toUpperCase() || "?"
            )}
            {isPlatinumMember ? (
              <span className="lead-card-platinum-badge"><i className="fa fa-gem"></i></span>
            ) : isGoldMember ? (
              <span className="lead-card-gold-badge"><i className="fa fa-crown"></i></span>
            ) : lastCompletedFlow ? (
              <span className="lead-card-trophy"><i className="fa fa-trophy"></i></span>
            ) : null}
          </div>
          <div className="lead-card-title">
            <h3>
              {lead.business_name || "Unknown"}
              {isPlatinumMember && <span className="platinum-member-tag">Platinum Connect</span>}
              {isGoldMember && <span className="gold-member-tag">Gold Connect</span>}
            </h3>
            <div className="lead-card-dates">
              {isPlatinumMember && (
                <span className="lead-card-date platinum-referrals">
                  <i className="fa fa-gem"></i>
                  {referralCount} Referrals
                </span>
              )}
              {isGoldMember && (
                <span className="lead-card-date gold-referrals">
                  <i className="fa fa-users"></i>
                  {referralCount} Referrals
                </span>
              )}
              {lastEmailTimestamp && (
                <span className="lead-card-date">
                  <i className="fa fa-paper-plane"></i>
                  Last Contacted: {getDaysAgo(lastEmailTimestamp)}
                </span>
              )}
              {lead.lead_since && (
                <span className="lead-card-date lead-since">
                  <i className="fa fa-star"></i>
                  Lead Since: {getDaysAgo(lead.lead_since)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Lead Tags */}
        {lead.tags && Object.keys(lead.tags).length > 0 && (
          <div className="lead-card-tags">
            {Object.values(lead.tags).slice(0, 4).map((tag, idx) => (
              <span 
                key={idx} 
                className="lead-tag-pill"
                style={{ backgroundColor: tag.color || '#6b7280' }}
                title={tag.name}
              >
                {tag.icon && <i className={`fa ${tag.icon}`}></i>}
                <span>{tag.name}</span>
              </span>
            ))}
            {Object.keys(lead.tags).length > 4 && (
              <span className="lead-tag-more">+{Object.keys(lead.tags).length - 4}</span>
            )}
          </div>
        )}

        {lead.lead_description && (
          <p className="lead-card-description">
            {lead.lead_description.length > 120
              ? lead.lead_description.substring(0, 120) + "..."
              : lead.lead_description}
          </p>
        )}


        {/* Notes Preview */}
        {(() => {
          const notesText = leadNotesToText(lead.lead_notes);
          if (!notesText) return null;
          return (
            <div className="lead-card-notes">
              <i className="fa fa-sticky-note"></i>
              <span>
                {notesText.length > 80 ? notesText.substring(0, 80) + "..." : notesText}
              </span>
            </div>
          );
        })()}


        {/* Referral Badge */}
        {lead.lead_referral_by && (() => {
          const referrer = leads.find(l => l.id === lead.lead_referral_by);
          if (!referrer) return null;
          return (
            <div 
              className="lead-card-referral"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLead(referrer);
              }}
              title={`Referred by ${referrer.business_name}`}
            >
              <i className="fa fa-user-friends"></i>
              <span>Referred by <strong>{referrer.business_name}</strong></span>
              <i className="fa fa-chevron-right"></i>
            </div>
          );
        })()}

        {/* E-Mail History Button */}
        {hasEmail && (
          <button 
            className="lead-card-emails-btn"
            onClick={(e) => {
              e.stopPropagation();
              // Nur E-Mail Modal öffnen, nicht Lead Modal
              loadEmailsForLead(lead.id, lead.lead_email);
              setExpandedEmails(prev => ({ ...prev, [lead.id]: true, showModal: lead.id }));
            }}
          >
            <i className="fa fa-bars"></i>
            <span>View Email History</span>
            <i className="fa fa-chevron-right"></i>
          </button>
        )}

        <div className="lead-card-bottom-row">
          <div className="lead-card-links">
            {hasEmail && (
              <a
                href={`mailto:${lead.lead_email}`}
                className="lead-card-link email"
                onClick={(e) => e.stopPropagation()}
                title={lead.lead_email}
              >
                <i className="fa fa-envelope"></i>
              </a>
            )}
            {hasWebsite && (
              <a
                href={lead.lead_website.startsWith("http") ? lead.lead_website : `https://${lead.lead_website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="lead-card-link website"
                onClick={(e) => e.stopPropagation()}
                title={lead.lead_website}
              >
                <i className="fa fa-globe"></i>
              </a>
            )}
            {hasInstagram && (
              <a
                href={`https://instagram.com/${lead.lead_instagram.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="lead-card-link instagram"
                onClick={(e) => e.stopPropagation()}
                title={`@${lead.lead_instagram.replace("@", "")}`}
              >
                <i className="fab fa-instagram"></i>
              </a>
            )}
            {hasWhatsApp && (
              <a
                href={`https://wa.me/${lead.lead_phone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="lead-card-link whatsapp"
                onClick={(e) => e.stopPropagation()}
                title={`WhatsApp: ${lead.lead_phone}`}
              >
                <i className="fab fa-whatsapp"></i>
              </a>
            )}
          </div>

          {/* Touchpoints Badge */}
          {((lead.touchpoints && Object.keys(lead.touchpoints).length > 0) || lead.lead_last_email?.timestamp) && (
            <div className="lead-card-touchpoints">
              <span className="lead-card-touchpoints-total">
                {(() => {
                  const manualPoints = lead.touchpoints 
                    ? Object.entries(lead.touchpoints).reduce((sum, [key, value]) => {
                        const tp = ALL_TOUCHPOINTS[key];
                        if (!tp) return sum;
                        const pointValue = tp.points || 1;
                        const count = tp.hasCount ? (value || 1) : 1;
                        return sum + (pointValue * count);
                      }, 0)
                    : 0;
                  const emailPoints = lead.emailHistory?.length || (lead.lead_last_email?.timestamp ? 1 : 0);
                  const total = Math.round((manualPoints + emailPoints) * 10) / 10;
                  return total;
                })()}
              </span>
            </div>
          )}
        </div>
      </div>
      </div>
    );
  };

  if (!loading && leads.length === 0 && !showTrash) {
    return (
      <>
        <LeadsEmptyStart
          canWrite={canWrite()}
          onAddLead={() => setShowNewLeadModal(true)}
          onOpenInbox={() => navigate('/world?view=smartInbox')}
          onOpenFlows={() => navigate('/world?view=settings&tab=leadSettings')}
        />
        {showNewLeadModal && (
          <LeadDetailModal
            lead={{}}
            isNew={true}
            onClose={() => setShowNewLeadModal(false)}
            onSaveField={handleSaveField}
            onCreateLead={handleCreateLead}
            allLeads={leads}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="leads-container leads-page">
        <div className={`leads-header ${showHeaderAnimation ? 'header-fade-in' : ''}`}>
          <div className="leads-header-left">
            <h1 className="leads-title">Leadflow</h1>
            <span className="leads-count">{filteredLeads.length} active leads</span>
            {activityStats.last24h > 0 && (
              <span className="leads-header-touchpoints-badge">
                <i className="fa fa-fire leads-header-tp-icon"></i>
                <span className="leads-header-tp-count">{activityStats.last24h}</span>
                <span className="leads-header-tp-label">today</span>
              </span>
            )}
          </div>
          <div className="leads-header-right">
            {/* Trash Toggle */}
            <button 
              className={`leads-trash-toggle ${showTrash ? 'active' : ''}`}
              onClick={() => setShowTrash(!showTrash)}
              title={showTrash ? "Back to Leads" : "View Trash"}
            >
              <i className={`fa ${showTrash ? 'fa-arrow-left' : 'fa-trash-alt'}`}></i>
              <span>{showTrash ? 'Back' : 'Trash'}</span>
              {!showTrash && trashedLeads.length > 0 && (
                <span className="leads-trash-badge">{trashedLeads.length}</span>
              )}
            </button>
            
            {/* Sort Toggle - nur in normaler Ansicht */}
            {!showTrash && (
            <div className="leads-sort-toggle">
              <button 
                className={`leads-sort-btn ${sortMode === 'recent' ? 'active' : ''}`}
                onClick={() => setSortMode('recent')}
                title="Sort by recently added"
              >
                <i className="fa fa-calendar-plus"></i>
                <span>Recent</span>
              </button>
              <button 
                className={`leads-sort-btn ${sortMode === 'todos' ? 'active' : ''}`}
                onClick={() => setSortMode('todos')}
                title="Sort by pending actions"
              >
                <i className="fa fa-tasks"></i>
                <span>To-Dos</span>
                {(() => {
                  const now = Date.now();
                  const dueCount = leads.filter(l => 
                    l.activeFlow && 
                    l.activeFlow.currentStepIndex >= 0 && 
                    now >= (l.activeFlow.nextStepAvailableAt || 0)
                  ).length;
                  return dueCount > 0 ? (
                    <span className="leads-sort-badge">{dueCount}</span>
                  ) : null;
                })()}
              </button>
            </div>
            )}
            {/* Search - nur in normaler Ansicht */}
            {!showTrash && (
            <div className="leads-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            )}
            
            {/* Add Lead Button - nur in normaler Ansicht */}
            {!showTrash && (
            <button 
              className="leads-add-btn"
              onClick={() => setShowNewLeadModal(true)}
              title="Add new lead"
            >
              <span>Add Lead</span>
              <span className="leads-add-btn-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </span>
            </button>
            )}
            
            {/* Empty Trash Button - nur in Trash-Ansicht und nur für Owner */}
            {showTrash && currentUserIsOwner && trashedLeads.length > 0 && (
            <button 
              className="leads-empty-trash-btn"
              onClick={emptyTrash}
              title="Permanently delete all trashed leads"
            >
              <i className="fa fa-trash"></i>
              <span>Empty Trash ({trashedLeads.length})</span>
            </button>
            )}
          </div>
        </div>

        {/* Due Actions Banner - Shows leads with ready flow steps */}
        {!loading && (() => {
          const now = Date.now();
          
          // Finde alle Leads mit fälligen Steps
          const dueLeads = leads.filter(lead => {
            if (!lead.activeFlow || lead.activeFlow.currentStepIndex < 0) return false;
            const stepAvailableAt = lead.activeFlow.nextStepAvailableAt || 0;
            return now >= stepAvailableAt;
          });

          if (dueLeads.length === 0) return null;

          return (
            <div className="leads-due-actions-banner">
              <div className="due-actions-section due-now">
                <div className="due-actions-header">
                  <i className="fa fa-bell"></i>
                  <span className="due-actions-title">
                    {dueLeads.length} Action{dueLeads.length !== 1 ? 's' : ''} Due Now
                  </span>
                </div>
                <div className="due-actions-list">
                  {dueLeads.slice(0, 5).map(lead => {
                    const flow = retargetingFlows.find(f => f.id === lead.activeFlow?.flowId);
                    const currentStep = flow?.steps?.[lead.activeFlow?.currentStepIndex];
                    
                    // Bei Conditions die tatsächliche Frage anzeigen
                    let touchpoint = null;
                    if (currentStep) {
                      if (currentStep.nodeType === 'condition') {
                        const conditionLabels = {
                          shown_interest: 'Has Shown Interest?',
                          qualified: 'Is Qualified?',
                          decision_maker: 'Is Decision Maker?',
                          has_budget: 'Has Budget?',
                          good_fit: 'Good Fit?',
                          ready_to_buy: 'Ready to Buy?',
                          needs_follow_up: 'Needs Follow Up?',
                          meeting_went_well: 'Meeting Went Well?',
                          responded_positively: 'Responded Positively?',
                          custom_question: currentStep.customQuestion || 'Custom Question',
                          meeting_booked: 'Meeting Booked?',
                          deal_closed: 'Deal Closed?',
                          email_opened: 'Email Opened?',
                          email_clicked: 'Email Clicked?',
                          replied: 'Replied?',
                          score_above: 'Score Above?',
                          has_phone: 'Has Phone?',
                          has_email: 'Has Email?',
                          has_company: 'Has Company?',
                        };
                        touchpoint = {
                          label: conditionLabels[currentStep.condition] || currentStep.condition,
                          icon: 'fa-question-circle'
                        };
                      } else if (currentStep.nodeType === 'end' || currentStep.type === 'end') {
                        touchpoint = { label: "End This Flow", icon: "fa-flag-checkered" };
                      } else {
                        const tp = ALL_TOUCHPOINTS[currentStep.type];
                        touchpoint = tp 
                          ? { ...tp, label: tp.readyLabel || tp.label }
                          : { label: currentStep.type, icon: "fa-circle" };
                      }
                    }
                    
                    // Berechne wie lange überfällig
                    const overdueSince = now - (lead.activeFlow?.nextStepAvailableAt || now);
                    const formatOverdue = (ms) => {
                      if (ms < 60 * 1000) return "just now";
                      const minutes = Math.floor(ms / (60 * 1000));
                      const hours = Math.floor(ms / (60 * 60 * 1000));
                      const days = Math.floor(ms / (24 * 60 * 60 * 1000));
                      if (days > 0) return `${days}d overdue`;
                      if (hours > 0) return `${hours}h overdue`;
                      return `${minutes}m overdue`;
                    };
                    
                    // Bestimme den Action-Link basierend auf Touchpoint-Typ und Lead-Daten
                    const getActionLink = () => {
                      const stepType = currentStep?.type;
                      if (!stepType) return null;
                      
                      // WhatsApp Actions
                      if (stepType === 'whatsapp_message' && lead.lead_phone && lead.lead_phone_whatsapp) {
                        return {
                          url: `https://wa.me/${lead.lead_phone.replace(/[^0-9]/g, '')}`,
                          label: 'Send Message',
                          icon: 'fab fa-whatsapp'
                        };
                      }
                      
                      // Instagram Actions
                      if (['instagram_dm', 'instagram_follow', 'instagram_like', 'instagram_comment'].includes(stepType) && lead.lead_instagram) {
                        const labels = {
                          instagram_dm: 'Send DM',
                          instagram_follow: 'Follow',
                          instagram_like: 'Like Post',
                          instagram_comment: 'Comment'
                        };
                        return {
                          url: `https://instagram.com/${lead.lead_instagram.replace('@', '')}`,
                          label: labels[stepType] || 'Open Instagram',
                          icon: 'fab fa-instagram'
                        };
                      }
                      
                      // LinkedIn Actions
                      if (['linkedin_message', 'linkedin_connect'].includes(stepType) && lead.lead_linkedin) {
                        return {
                          url: lead.lead_linkedin.startsWith('http') ? lead.lead_linkedin : `https://linkedin.com/in/${lead.lead_linkedin}`,
                          label: stepType === 'linkedin_message' ? 'Send Message' : 'Connect',
                          icon: 'fab fa-linkedin'
                        };
                      }
                      
                      // Phone Call
                      if (stepType === 'phone_call' && lead.lead_phone) {
                        return {
                          url: `tel:${lead.lead_phone}`,
                          label: 'Call Now',
                          icon: 'fa fa-phone'
                        };
                      }
                      
                      return null;
                    };
                    
                    const actionLink = getActionLink();
                    
                    return (
                      <div 
                        key={lead.id} 
                        className="due-action-item"
                        onClick={() => setSelectedLead(lead)}
                      >
                        <div className="due-action-avatar">
                          {lead.business_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="due-action-info">
                          <span className="due-action-name">{lead.business_name}</span>
                          {touchpoint && (
                            <span className="due-action-step">
                              <i className={`${touchpoint.isBrand ? 'fab' : 'fa'} ${touchpoint.icon}`}></i>
                              {touchpoint.label}
                              {overdueSince > 60 * 1000 && (
                                <span className="due-action-overdue">• {formatOverdue(overdueSince)}</span>
                              )}
                            </span>
                          )}
                          {currentStep?.note && (
                            <span className="due-action-note">
                              "{currentStep.note}"
                            </span>
                          )}
                        </div>
                        <div className="due-action-buttons">
                          {actionLink && (
                            <a 
                              href={actionLink.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="due-action-link-btn"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className={actionLink.icon}></i>
                              {actionLink.label}
                            </a>
                          )}
                          {/* Bei Conditions: Yes/No Buttons, sonst nur Check */}
                          {currentStep?.nodeType === 'condition' ? (
                            <>
                              <button 
                                className="due-action-btn due-action-btn-no"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  completeFlowStepWithDecision(lead.id, lead.activeFlow.currentStepIndex, 'no');
                                }}
                                title="No"
                              >
                                <i className="fa fa-times"></i>
                              </button>
                              <button 
                                className="due-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  completeFlowStepWithDecision(lead.id, lead.activeFlow.currentStepIndex, 'yes');
                                }}
                                title="Yes"
                              >
                                <i className="fa fa-check"></i>
                              </button>
                            </>
                          ) : (
                            <button 
                              className="due-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                completeFlowStep(lead.id, lead.activeFlow.currentStepIndex);
                              }}
                            >
                              <i className="fa fa-check"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {dueLeads.length > 5 && (
                    <div className="due-actions-more">
                      +{dueLeads.length - 5} more actions pending
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div className="leads-loading-container">
            <div className="leads-loading-bar-container">
              <div className="leads-loading-logo-spin">
                <img src="/img/salesgod_dark.webp" alt="SalesGod" />
              </div>
              <div className="leads-loading-bar">
                <div className="leads-loading-bar-fill"></div>
              </div>
            </div>
          </div>
        ) : filteredLeads.length === 0 && !showTrash ? (
          <div className="leads-empty">
            <h2>No matching leads</h2>
            <p>Nothing in this view matches the current search.</p>
          </div>
        ) : showTrash ? (
          /* ========== PAPIERKORB ANSICHT ========== */
          <div className="leads-trash-container">
            {trashedLeads.length === 0 ? (
              <div className="leads-trash-empty">
                <i className="fa fa-trash-alt"></i>
                <h3>Trash is empty</h3>
                <p>Deleted leads will appear here</p>
              </div>
            ) : (
              <div className="leads-grid leads-trash-grid">
                {trashedLeads.map((lead) => (
                  <div key={lead.id} className="lead-card lead-card-trashed">
                    <div className="lead-card-header">
                      <div className="lead-card-avatar">
                        {lead.lead_avatar ? (
                          <img src={lead.lead_avatar} alt="" />
                        ) : (
                          <span>{(lead.business_name || (() => { try { const dms = lead.lead_decision_makers ? (typeof lead.lead_decision_makers === 'string' ? JSON.parse(lead.lead_decision_makers) : lead.lead_decision_makers) : []; return dms[0]?.name; } catch(e) { return null; } })() || lead.lead_firstname || '?')[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="lead-card-info">
                        <h4 className="lead-card-name">{lead.business_name || (() => { try { const dms = lead.lead_decision_makers ? (typeof lead.lead_decision_makers === 'string' ? JSON.parse(lead.lead_decision_makers) : lead.lead_decision_makers) : []; if (dms[0]?.name) return dms[0].name; } catch(e) {} return lead.lead_firstname ? `${lead.lead_firstname} ${lead.lead_lastname || ''}`.trim() : 'Unknown'; })()}</h4>
                        {lead.lead_email && <span className="lead-card-email">{lead.lead_email}</span>}
                      </div>
                    </div>
                    
                    <div className="lead-card-trash-info">
                      <div className="trash-info-row">
                        <i className="fa fa-clock"></i>
                        <span>Deleted {new Date(lead.trashedAt).toLocaleDateString()}</span>
                      </div>
                      {lead.trashedByEmail && (
                        <div className="trash-info-row trash-deleted-by">
                          <i className="fa fa-user"></i>
                          <span>by {lead.trashedByEmail}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="lead-card-trash-actions">
                      <button 
                        className="lead-trash-restore-btn"
                        onClick={() => restoreFromTrash(lead.id, lead.business_name, lead)}
                        title="Restore lead"
                      >
                        <i className="fa fa-undo"></i>
                        Restore
                      </button>
                      
                      {currentUserIsOwner && (
                        <button 
                          className="lead-trash-delete-btn"
                          onClick={() => deletePermanently(lead.id, lead.business_name)}
                          title="Delete permanently"
                        >
                          <i className="fa fa-times"></i>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ========== NORMALE LEADS ANSICHT ========== */
          <>
            <div className="leads-grid">
              {filteredLeads.map((lead) => (
                <LeadCard key={lead.id} lead={lead} />
              ))}
            </div>
          </>
        )}
      </div>

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onSaveField={handleSaveField}
          allLeads={leads}
          onSelectLead={(newLead) => setSelectedLead(newLead)}
        />
      )}

      {showNewLeadModal && (
        <LeadDetailModal
          lead={{}}
          isNew={true}
          onClose={() => setShowNewLeadModal(false)}
          onSaveField={handleSaveField}
          onCreateLead={handleCreateLead}
          allLeads={leads}
        />
      )}

      {/* Flow Select Modal */}
      {flowSelectModal && (
        <div className="flow-select-modal-overlay" onClick={() => setFlowSelectModal(null)}>
          <div className={`flow-select-modal ${retargetingFlows.length === 0 ? 'empty-state' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button className="flow-select-modal-close" onClick={() => setFlowSelectModal(null)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            {retargetingFlows.length > 0 ? (
              <>
                <div className="flow-select-header">
                  <div className="flow-select-icon">
                    <i className="fa fa-route"></i>
                  </div>
                  <h2>Select Automation Flow</h2>
                  <p>Choose a flow to automate follow-ups for this lead</p>
                </div>
                
                <div className="flow-select-list">
                  {retargetingFlows.map(flow => (
                    <button
                      key={flow.id}
                      className="flow-select-item"
                      onClick={() => {
                        startFlowForLead(flowSelectModal, flow.id);
                        setFlowSelectModal(null);
                      }}
                    >
                      <div className="flow-select-item-icon">
                        <i className="fa fa-play"></i>
                      </div>
                      <div className="flow-select-item-info">
                        <span className="flow-select-item-name">{flow.name}</span>
                        <span className="flow-select-item-steps">
                          {flow.steps?.length || 0} steps
                        </span>
                      </div>
                      <div className="flow-select-item-arrow">
                        <i className="fa fa-chevron-right"></i>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="flow-select-empty">
                <div className="flow-select-empty-icon">
                  <i className="fa fa-route"></i>
                </div>
                <h4>No Flows Created Yet</h4>
                <p>Create a retargeting flow to automate follow-ups.</p>
                <a href="/world?view=settings&tab=leadSettings" className="flow-select-empty-btn">
                  <i className="fa fa-magic"></i>
                  Create Your First Flow
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trash Confirm Popup */}
      {trashConfirmModal && (
        <div className="trash-confirm-overlay" onClick={() => setTrashConfirmModal(null)}>
          <div className="trash-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trash-confirm-icon">
              <i className="fa fa-trash-alt"></i>
            </div>
            <h3>Move to Trash?</h3>
            <p>
              Are you sure you want to move <strong>"{trashConfirmModal.leadName || 'this lead'}"</strong> to trash?
            </p>
            <p className="trash-confirm-hint">
              You can restore it later from the trash.
            </p>
            <div className="trash-confirm-actions">
              <button 
                className="trash-confirm-cancel"
                onClick={() => setTrashConfirmModal(null)}
              >
                Cancel
              </button>
              <button 
                className="trash-confirm-delete"
                onClick={confirmMoveToTrash}
                disabled={removingLead === trashConfirmModal.leadId}
              >
                {removingLead === trashConfirmModal.leadId ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    Moving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-trash-alt"></i>
                    Move to Trash
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* E-Mail Thread Modal */}
      {expandedEmails.showModal && (() => {
        const modalLead = leads.find(l => l.id === expandedEmails.showModal);
        if (!modalLead) return null;
        
        return (
          <div className="email-thread-modal-overlay" onClick={() => {
            setExpandedEmails(prev => ({ ...prev, showModal: null }));
          }}>
            <div className="email-thread-modal" onClick={(e) => e.stopPropagation()}>
              <button className="email-thread-modal-close" onClick={() => {
                setExpandedEmails(prev => ({ ...prev, showModal: null }));
              }}>
                <i className="fa fa-times"></i>
              </button>

              <div className="email-thread-header">
                <h2>
                  <i className="fa fa-envelope"></i>
                  Email History with {modalLead.business_name || "Unknown"}
                </h2>
                <p className="email-thread-count">
                  {leadEmails[modalLead.id]?.length || 0} emails
                </p>
              </div>

              <div className="email-thread-list">
                {loadingEmails[modalLead.id] ? (
                  <div className="email-thread-loading">
                    <i className="fa fa-spinner fa-spin"></i>
                    <span>Loading emails...</span>
                  </div>
                ) : leadEmails[modalLead.id]?.length > 0 ? (
                  leadEmails[modalLead.id].map((email) => (
                    <div 
                      key={email.id} 
                      className={`email-thread-item ${email.emailDirection} expanded`}
                    >
                      <div className="email-thread-item-header">
                        <div className={`email-thread-direction ${email.emailDirection}`}>
                          <i className={`fa ${email.emailDirection === 'sent' ? 'fa-paper-plane' : 'fa-inbox'}`}></i>
                        </div>
                        <div className="email-thread-item-info">
                          <span className="email-thread-subject">{email.subject || "(No Subject)"}</span>
                          <span className="email-thread-meta">
                            {email.emailDirection === 'sent' ? 'To: ' : 'From: '}
                            {email.emailDirection === 'sent' ? (email.to || email.recipients) : email.from}
                          </span>
                        </div>
                        <div className="email-thread-item-right">
                          <span className="email-thread-date">
                            {formatEmailDate(email.timestamp || email.sentAt)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="email-thread-item-body">
                        {email.htmlStorageUrl ? (
                          <iframe
                            src={email.htmlStorageUrl}
                            className="email-content-iframe"
                            title="Email Content"
                            sandbox="allow-same-origin allow-popups"
                          />
                        ) : (() => {
                          // Finde den HTML Content - manchmal ist er in text/bodyText gespeichert
                          const possibleHtml = email.bodyHtml || email.html || email.text || email.bodyText || email.content || "";
                          const plainText = email.preview || email.text || email.bodyText || email.content || "";
                          
                          // Prüfe ob der Content HTML enthält
                          const isHtml = possibleHtml.includes('<div') || 
                                        possibleHtml.includes('<p') || 
                                        possibleHtml.includes('<table') ||
                                        possibleHtml.includes('<br') ||
                                        possibleHtml.includes('style=');
                          
                          return (
                            <div className="email-letter-container">
                              <Letter 
                                html={isHtml ? possibleHtml : ""}
                                text={isHtml ? "" : plainText}
                                useIframe={false}
                                allowedSchemas={['http', 'https', 'mailto']}
                                className="email-letter-content"
                              />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="email-thread-empty">
                    <i className="fa fa-inbox"></i>
                    <span>No emails found</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========== FIXED ACTIVITY SCOREBOARD (erscheint für 3 Sek. nach Touchpoint) ========== */}
      {scoreboardVisible && (
        <div className={`leads-activity-scoreboard ${scoreboardFading ? 'fading' : ''}`}>
          <button 
            className="scoreboard-close-btn"
            onClick={() => {
              setScoreboardFading(true);
              setTimeout(() => {
                setScoreboardVisible(false);
                setScoreboardFading(false);
              }, 300);
            }}
            title="Close"
          >
            <i className="fa fa-times"></i>
          </button>
          
          <div className="activity-scoreboard-main">
            <div className="activity-scoreboard-fire">
              <span className="fire-icon">🔥</span>
              <div className="fire-stats">
                <span className="fire-count">{activityStats.last24h}</span>
                <span className="fire-label">Touchpoints Today</span>
              </div>
            </div>
            <div className="activity-scoreboard-points">
              <span className="points-value">{Math.round((activityStats.totalPoints24h || 0) * 10) / 10}</span>
              <span className="points-label">Points</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Leads;
